/**
 * Every string the interface shows, in English and Persian (ADR-0023).
 *
 * Plain objects rather than an i18n library. Persian is written as `fa` typed
 * as `Messages`, so a key added to one language and forgotten in the other is a
 * type error rather than an English word surfacing in a Persian page. Anything
 * with a number in it is a function: English plurals, and Persian digits —
 * "۳ فصل", not "3 فصل" — are the language's job, not the caller's.
 *
 * No arrows in messages: back and forward are drawn chevrons (BackLink,
 * icons.tsx) that flip with the interface's direction.
 *
 * Not here: authored text (titles, chapters, answers), which is in whatever
 * language it was written; server error details, which stay English; and money,
 * which the server formats as dollars.
 */

export type Locale = "en" | "fa";

export const LOCALES: Locale[] = ["en", "fa"];

/** Each language named in itself, so the switch reads right from either side. */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: "English",
  fa: "فارسی",
};

const enNum = new Intl.NumberFormat("en-US");
const faNum = new Intl.NumberFormat("fa-IR");
const n = (x: number) => enNum.format(x);
const f = (x: number) => faNum.format(x);
const s = (count: number, one: string, many: string) => (count === 1 ? one : many);

const en = {
  meta: {
    num: n,
    /** For dates. The usage period is a Gregorian month, so Persian keeps
     *  that calendar rather than fa-IR's default Solar Hijri one. */
    dateLocale: "en-US",
  },
  common: {
    tryAgain: "Try again",
    close: "Close",
    somethingWrong: "Something went wrong.",
    searchLanguage: "Search for a language",
    noLanguage: "No language matches.",
    somethingWrongTryAgain: "Something went wrong. Try again.",
    /** Labels the server's own record of a failure, so a report from a phone
     *  quotes it instead of a sentence identical for every one (ADR-0092). The
     *  id is rendered beside it in a <bdi>: it is a Latin run with hyphens, and
     *  in a Persian line they would otherwise swap ends. */
    failureReference: "Reference",
    delete: "Delete",
    keep: "Keep",
    characters: (c: number) => `${n(c)} ${s(c, "character", "characters")}`,
    language: "Language",
    /** Names a region whose placeholders are standing in for it (ADR-0068). */
    loading: "Loading",
    loadingChapter: "Loading the chapter",
  },
  nav: {
    /** The wordmark beside the logo, and the tab title. */
    brand: "SageLeaf",
    home: "SageLeaf home",
    library: "Library",
    browse: "Browse",
    studio: "Studio",
    usage: "Usage",
    admin: "Admin",
    signIn: "Sign in",
    signOut: "Sign out",
    signOutFailed: "You are still signed in. Try again.",
    settings: "Settings",
    myQuotes: "My quotes",
    accountMenu: (name: string) => `Account menu for ${name}`,
    /** The header's sun/moon: says what pressing it does. */
    toDark: "Switch to dark",
    toLight: "Switch to light",
  },
  quotes: {
    title: "My quotes",
    empty:
      "Nothing kept yet. In the app, long-press a line, or tap Ask while one is highlighted, then tap Remember.",
    failed: "Your quotes did not load.",
    retry: "Try again",
    /** A row's Share: the quote as a picture (requirement 0138). */
    share: "Share as a picture",
    making: "Making the picture…",
    download: "Download",
    shareTo: "Share",
    close: "Close",
    tooLong:
      "This quote is too long for a picture. Keep a shorter part of it from the chapter and share that.",
    failedPicture: "The picture could not be made. Try again.",
    place: (book: string, chapter: string) =>
      chapter ? `\u2068${book}\u2069 · \u2068${chapter}\u2069` : `\u2068${book}\u2069`,
  },
  drawer: {
    menu: "Menu",
    closeMenu: "Close menu",
    contents: "Contents",
    editChapter: "Edit this chapter in the studio",
    chapters: "Chapters",
  },
  settings: {
    title: "Settings",
    back: "Back",
    profile: "Profile",
    profileNote:
      "Your account, and the language this app speaks to you in. Kept with your account.",
    reading: "Reading",
    readingNote: "How chapters look in the reader.",
    account: "Account",
    deleteAccount: "Delete account",
    deleteNote:
      "Deletes your account, your reading progress and library, and any books you made that nobody else reads. A book you published stays for its readers.",
    deleteQuestion: "Delete your account for good? This cannot be undone.",
    deleteFailed: "Your account could not be deleted. Nothing was removed.",
    textSize: "Text size",
    smaller: "Smaller text",
    larger: "Larger text",
    sizeOf: (i: number, total: number) => `Text size ${n(i)} of ${n(total)}`,
    typeface: "Typeface",
    typefaceNote: "Persian is always set in Vazirmatn, whichever face you choose.",
    theme: "Theme",
    themes: { auto: "Auto", light: "Light", sepia: "Sepia", dark: "Dark", night: "Night" },
    autoplay: "Auto-play next chapter",
    autoplayNote: "When a chapter you are listening to ends, the next one starts after 45 seconds.",
    sample: "Sample",
    sampleText:
      "In life there are wounds that, like a canker, slowly eat away at the soul in solitude and wear it down.",
  },
  reader: {
    download: "Download the whole chapter",
    chapterNav: "Other chapters",
    previousChapter: "Previous chapter",
    nextChapter: "Next chapter",
    nextChapterIn: (seconds: number) => `Next chapter in ${n(seconds)} s`,
    readSoFar: "Read so far",
    /** A block's tag when it has no label of its own (ADR-0154). */
    blockNote: "Note",
    blockOriginal: "Original",
    preparing: "Preparing the file…",
    saveMp3: "Save the whole chapter (MP3)",
    downloadSlow:
      "Still being made — a long chapter takes a few minutes. It carries on without this page open; come back and press Download again.",
    downloadFailed: "Could not prepare the download.",
    characters: "Characters",
    castEmpty: "Characters appear here as you meet them.",
    castSummary: (met: number, unmet: number) =>
      met === 0
        ? "You meet them as you listen."
        : `${n(met)} met so far` + (unmet > 0 ? ` · ${n(unmet)} still to come` : ""),
    arcsTitle: "Character arcs",
    spoilers: "Spoilers",
    arcsWarning: "How everyone's story ends — including chapters you haven't reached.",
    showArcs: "Reveal",
    hideArcs: "Hide",
    arcsLoading: "Loading…",
    castFailed: "The characters could not be loaded.",
  },
  signIn: {
    /**
     * Why a sign-in did not happen (ADR-0084). The keys are the server's
     * redirect vocabulary in internal/httpapi/session.go; nothing checks that
     * the two agree, so the client falls back to `failed`.
     *
     * `conflict` deliberately says nothing about the other account — not even
     * whether one exists — and promises nobody is looking, because nobody is:
     * there is no support path, and the owner resolves it from the audit row.
     */
    reasons: {
      declined: "Sign-in was cancelled. You can try again whenever you like.",
      expired: "That sign-in took too long and has expired. Please try again.",
      disabled: "This account has been disabled.",
      conflict:
        "We could not sign you in: this email address is already registered to a different account.",
      failed: "We could not sign you in. Please try again.",
    },
  },
  landing: {
    // Provisional (requirement 0079): plan.md takes the final words from
    // testers. Warm, a companion at your side: never "hard" books (the owner
    // finds it repelling, 2026-10-01), never "classics", never "free" on its
    // own, and no other readers or book club, since there are none. Every line
    // says what a visitor can do (owner, 2026-10-19).
    title: "You're not reading it alone",
    blurb:
      "Read or listen, and ask about any line as you go. Answers come from the pages you have read, never the ones ahead. Every character has a face beside their words, and in narrated books a voice.",
    start: "Start reading",
    startNote: "Opens here, no account needed.",
    signIn: "Sign in to start",
    heroAlt:
      "Hamlet narrated in SageLeaf: the spoken words highlighted, Barnardo's portrait beside his lines",
    facesTitle: "Meet the cast as they enter",
    facesBody:
      "Characters appear when the story introduces them. The ones still to come stay hidden.",
    features: [
      {
        title: "Ask without spoilers",
        body: "Tap any line, then Ask. The answer comes from the pages behind you, never the ones ahead, so the ending stays yours.",
        alt: "An answer to “Why does the ghost look like the old King?”, from the book so far",
      },
      {
        title: "Every book opens on its own desk",
        body: "A book lies on a reading-room desk, in daylight or by candle. Tap it and the pages open where you stopped.",
        alt: "Hamlet lying on the reading-room desk, with its chapters below",
      },
      {
        title: "Pictures drawn from the page",
        body: "Scenes are painted from the text itself and placed where they happen in the chapter.",
        alt: "A chapter of Alice's Adventures in Wonderland with its picture of Alice and the White Rabbit",
      },
      {
        title: "A reading room of your own",
        body: "Your books stand on walnut shelves. Pick up where you left off in one tap, on the web or in the app.",
        alt: "The Library: the book to pick up again, and the shelves",
      },
    ],
    /** The feature clips: real recordings of the app, one per entry, in order. */
    clipsTitle: "See it work",
    clips: [
      {
        title: "Ask about any line",
        body: "Tap a line, then Ask. The answer comes from the pages you have read, never the ones ahead.",
      },
      {
        title: "Ask your own question",
        body: "Type a follow-up. It is answered from the same pages, so the ending stays yours.",
      },
      {
        title: "Say it more simply",
        body: "One tap turns an old line into plain modern English.",
      },
      {
        title: "Long-press a word",
        body: "Its meaning appears at once, with what it means in this line.",
      },
      {
        title: "Keep a line",
        body: "Save a sentence you want to remember, and it waits on your phone's home screen.",
      },
      {
        title: "Browse the collections",
        body: "Books gathered by theme, from the Stoics to the death of Socrates. Swipe and open one.",
      },
    ],
    clipPlay: "Play",
    clipPause: "Pause",
    more: "More books",
    /** The shelf of every public book a visitor sees (ADR-0197). */
    shelfTitle: "The whole shelf",
    legend: "Books marked Read now open here, no account needed. Plus opens every book.",
  },
  free: {
    title: "Free to read",
    note: "Anyone can read this book without an account, and it appears in the public gallery. Everything else needs signing in, including asking the SageLeaf agent about it.",
    on: "Anyone can read this.",
    off: "Only signed-in readers.",
    turnOn: "Make it free",
    turnOff: "Make it sign-in only",
    working: "Saving…",
    publishFirst: "Publish the book first — there is nothing for a visitor to read yet.",
    publicFirst:
      "Not everyone can read this book. Let everyone read it before offering it to visitors.",
    failed: "Could not change that. Try again.",
  },
  chat: {
    title: "Ask the book",
    /** The reader's Ask button, as the app labels it. */
    ask: "Ask",
    newChat: "New chat",
    /** What a question with no line is about (ADR-0176). */
    aboutPlace: (c: number) => `About chapter ${n(c)}, up to where you are`,
    empty: "Ask about a character, a word, or what happened earlier.",
    /** The same, for a book its author says tells no story (ADR-0158). */
    emptyNotStory: "Ask about a word, a name, or what a passage means.",
    withoutLine: "Ask without this line",
    remaining: (r: number) => `${n(r)} ${s(r, "question", "questions")} left this hour`,
    yourQuestion: "Your question",
    placeholder: "Ask about the book",
    placeholderFollowUp: "Ask a follow-up",
    send: "Send",
    reading: "Reading…",
    fromTheBook: "From the book",
    notInBook: "Nothing in the book answers this directly.",
    /**
     * The page-only label (ADR-0014, amended by ADR-0075). It used to read
     * "From this page only — search is off for this book", which stopped being
     * true when the model gained tools that read every chapter behind the
     * reader. The three labels must still never render alike: cited passages,
     * the book so far, and outside knowledge.
     */
    pageOnly: "From the book so far — not searched, and nothing ahead of you.",
    failed: "Could not answer that. Try again.",
    reopen: "Open the conversation about this line",
    whoIs: (name: string) => `Who is ${name}?`,
    /**
     * The one-tap questions (ADR-0141): each chip, and the turn it leaves in
     * the conversation. The word arrives isolated, so a Latin word keeps its
     * quotes in a Persian line.
     */
    /** The reader's turn when a long press asks a word's meaning (ADR-0141). */
    meaningOf: (word: string) => `Meaning of ${word}`,
    meaning: "Meaning",
    hardWords: "The hard words and idioms in this line",
    explain: "Explain",
    explainThis: "Explain this",
    simpler: "Simpler",
    sayItSimpler: "Say it more simply",
    soFar: "What do I know about them so far?",
    soFarNote: "Uses one question. Only what you have read is used.",
    soFarReading: "Reading back…",
    soFarFailed: "Could not answer that. Try again.",
    // The two answer modes (ADR-0071). Named so the reader knows what they
    // chose: open is not spoiler-free and is not offered as though it were.
    mode: "What it may answer from",
    modeSpoilerFree: "Spoiler-free",
    modeOpen: "Open",
    modeSpoilerFreeNote: "Only what you have read. Nothing ahead of you.",
    modeOpenNote:
      "The whole book, including what is ahead of you, plus what it knows about the work. May give things away.",
    openAnswer: "Includes what it knows beyond the book.",
    // Above an open answer on a story book (owner, 2026-10-20; ADR-0188):
    // Open may read ahead, so the answer may give the story away.
    spoilerNote: "May contain spoilers.",
    spoilerSwitch: (mode: string) => `Tap to switch to ${mode}.`,
    nothingRead: "You are at the beginning, so there is nothing read yet to answer from.",
    askOpen: "Ask openly instead",
    signInToChat: "Sign in to chat with the SageLeaf agent about this book.",
    // The site-wide question ceiling (ADR-0191): reading and listening go on.
    questionsPaused: "Questions are paused for today — they're back at midnight Pacific.",
  },
  report: {
    /** The quiet entry in the Ask sheet, under the quoted line. */
    action: "Report a problem",
    /** The small controls on a portrait and a full-screen picture. */
    portrait: "Report a problem with this portrait",
    picture: "Report a problem with this picture",
    title: "Report a problem",
    note: "What is wrong? (optional)",
    send: "Send",
    sending: "Sending…",
    cancel: "Cancel",
    done: "Done",
    // Actionable (the owner, 10-19): what happens next, and that nothing
    // else is asked of the reader.
    sent: "Sent to the people who look after this book. They will check it and correct it in a later edition; you don't need to do anything else.",
    failed: "The report was not sent. Check your connection and try again.",
  },
  picture: {
    close: "Close picture",
    open: "Open this picture full screen",
  },
  player: {
    play: "Play",
    pause: "Pause",
    position: "Position in the chapter",
    positionOf: (at: string, total: string) => `${at} of ${total}`,
    follow: "Follow",
    /**
     * Said only once several quiet attempts have failed, and taken back the
     * moment sound returns: a phone that dropped its connection for four
     * seconds does not need telling.
     */
    playbackRetrying: "The audio stopped. Picking it back up…",
    playbackFailed: "Playback stopped. Press play to pick it back up.",
    /** A partial render: some of the text on screen has no audio (ADR-0063). */
    audioIncomplete: "Parts of this chapter have no audio yet, so the reading skips them.",
    speed: "Playback speed",
    speedOf: (rate: string) => `Playback speed, now ${rate}`,
    recordingTitle: "How this was narrated",
    recordingModel: "Speech model",
    recordingVoice: "Voice",
    recordingMode: "Mode",
    recordingModeFast: "Fast",
    recordingModeEconomy: "Economy",
    recordingParts: "Parts",
    recordingPartsValue: (parts: number, chars: number) =>
      `${n(parts)}, about ${n(chars)} characters each`,
    recordingExtras: "Also",
    recordingReadCut: "Each part read the sentence before it",
    recordingPrepared: "Text prepared for the narrator",
    recordingMade: "Recorded",
    recordingNote:
      "This is the audio you are hearing. Changing a setting does not change it until the chapter is narrated again.",
  },
  correction: {
    title: "Correct pronunciation",
    hintNoteIPA:
      "ElevenLabs can't follow a written hint, so it is turned into phonetic spelling (IPA), which ElevenLabs reads in place of the word. Listen to check it. To give the IPA yourself, type it between slashes, like /ɾæmeˈhɒ/.",
    sentAsIPA: (ipa: string) => `ElevenLabs reads the word as /${ipa}/`,
    hintsUnsupported: (model: string) =>
      `${model} is sent only the text, so a hint does nothing on this book. Respell the word and listen instead.`,
    respell: "Respell",
    sayItLike: "Say it like…",
    respellNote:
      "No spelling is reliably better — one that fixes one sentence mangles another. Listen before you keep it.",
    respellNoSuggestions:
      "The spellings suggested here are Persian ones — a short vowel, or a letter swapped for one that sounds the same — so there are none to offer for this word. Type a spelling that sounds right and listen to it.",
    typeSpelling: "or type a spelling",
    hintNote:
      "A hint goes in a preamble the model follows and never reads aloud — it is the only way to place stress, which the spelling often cannot write down. Latin letters belong here and never in a respelling.",
    hintPlaceholder: 'e.g. "khar-goo-SHEE"',
    rendering: "Rendering…",
    listen: "Listen",
    rendersOne: "renders this one sentence",
    leftThisHour: (r: number) => ` · ${n(r)} left this hour`,
    applyTo: "Apply to",
    scopeChapter: "this chapter",
    scopeBook: "this book",
    scopeOrg: "the whole library",
    existing: (scope: string) =>
      `Already corrected for ${scope}. Change it and save, or remove it.`,
    existingVariant: (scope: string) =>
      `A correction for a different spelling of this word is saved for ${scope}, so it does nothing here:`,
    saving: "Saving…",
    save: "Save",
    update: "Update",
    remove: "Remove",
    removing: "Removing…",
    savingNote: "Saving does not re-narrate — collect what you want, then regenerate once.",
  },
  edit: {
    editText: "Edit",
    editHint: "Edit the text and pictures",
    done: "Done",
    paragraph: (i: number) => `Paragraph ${n(i)}`,
    saving: "Saving…",
    saved: "Saved",
    unsaved: "Not saved yet",
    savesAsYouGo: "Saves as you go",
    saveFailed: "Could not save the text.",
    retry: "Try again",
    restored: "Changes you had not saved last time were put back.",
    staleCopy:
      "Changes you had not saved last time were made to an older version of this chapter, which has changed since.",
    useStaleCopy: "Use my unsaved version",
    discardStaleCopy: "Keep the newer text",
    passageMoved: "That passage has changed, so the version was not placed.",
    rewriteSelection: "Rewrite with AI",
    selectToRewrite: "Select some text to rewrite it with AI.",
    changeTitle: "Change this line",
    fixPronunciation: (word: string) => `Fix how “${word}” is pronounced`,
    editHere: "Edit the text here",
    rewriteHere: "Rewrite with AI",
    lineNotInText:
      "This line has changed since it was narrated, so it could not be selected in the text.",
    rewriteTitle: "Rewrite with AI",
    selected: "Selected passage",
    empty:
      "Say how this passage should change — shorter, warmer, in the first person. Each reply is a new version you can keep changing.",
    yourInstruction: "How it should change",
    placeholder: "How should it change?",
    placeholderFollowUp: "What else should change?",
    send: "Send",
    writing: "Writing…",
    version: (i: number) => `Version ${n(i)}`,
    replace: "Replace selection",
    insertAfter: "Insert after",
    failed: "Could not rewrite that. Try again.",
    notSaved: "Nothing is saved until you save the chapter.",
  },
  images: {
    // The picture in force, shown when the sheet opens on something that has one.
    current: "Current picture",
    addHere: "Add a picture here",
    addAtTop: "Add a picture at the top",
    title: "Picture for this passage",
    titleTop: "Picture for the top of the chapter",
    replaceTitle: "Replace this picture",
    replace: "Replace",
    describeTab: "Describe it",
    uploadTab: "Upload one",
    // Describing.
    empty:
      "The description is written from this passage and the text around it, and you can edit every word of it before anything is drawn.",
    suggest: "Suggest a description",
    placeholder: "What should the picture show?",
    seedHint: "Start it off in the box below — or send it empty and one will be suggested.",
    placeholderFollowUp: "What should change?",
    yourInstruction: "How the picture should change",
    send: "Send",
    writing: "Writing…",
    describeFailed: "Could not write a description. Try again.",
    description: "Description",
    descriptionHint: "Edit these words before drawing — they are the whole brief.",
    // Drawing.
    shape: "Shape",
    shapeNames: {
      "4:3": "Landscape",
      "3:4": "Portrait",
      "1:1": "Square",
      "16:9": "Wide",
    } as Record<string, string>,
    /** Which model draws it (ADR-0086). Never a default: the sheet opens on
     *  standard, and pro is a tap somebody makes on purpose. */
    quality: "Quality",
    qualityNames: {
      standard: "Standard",
      pro: "Best",
    } as Record<string, string>,
    draw: "Draw this picture",
    drawAgain: "Draw another",
    /** The primary button when words are waiting in the box (requirement 0030). */
    updateAndDraw: "Update and draw",
    updateWords: "Update the words only",
    /** Said after a describe turn, so a changed description is never silent. */
    descriptionUpdated: "Description updated — read it, then draw.",
    showBrief: "Show the description",
    hideBrief: "Hide the description",
    drawing: "Drawing…",
    updating: "Updating the description…",
    drawFailed: "Could not draw that. Change the description and try again.",
    costEach: (amount: string) => `${amount} each`,
    spentHere: (amount: string) => `${amount} spent on this picture so far`,
    // Candidates.
    candidates: "Pictures drawn",
    use: "Use this one",
    placing: "Placing…",
    placeFailed: "Could not place the picture. Reload the chapter and try again.",
    // Uploading.
    chooseFile: "Choose a picture",
    orDropImage: "or drop a JPEG, PNG or WebP here",
    uploading: "Uploading…",
    uploadFailed: "Could not upload that. It must be a JPEG, PNG or WebP under 12 MB.",
    // A placed picture.
    caption: "Caption",
    captionPlaceholder: "Caption, shown under the picture",
    alt: "Description for screen readers",
    altPlaceholder: "What the picture shows",
    altMissing: "No description for screen readers",
    /** A picture readers do not have yet, said quietly (ADR-0062). */
    notPublished: "Not published yet",
    save: "Save",
    saved: "Saved",
    remove: "Remove",
    removeConfirm: "Remove this picture from the chapter?",
    removeFailed: "That picture was not removed; it is still in the chapter.",
    saveFailed: "The caption was not saved.",
    moveFailed: "The picture was not moved.",
    move: "Move",
    moveHere: "Move it here",
    moving: "Choose where it goes",
    cancelMove: "Cancel",
    generatedNote: "Drawn from a description",
    // Continuity, made visible (ADR-0040).
    drawnFrom: (names: string[]) => `Drawn from: ${names.join(", ")}`,
    // Who a picture is drawn from, chosen with its description (ADR-0047).
    castLabel: "In this picture:",
    castAdd: "Add a character",
    castDone: "Done",
    castFull: "Four at most.",
    castRemove: (name: string) => `Take ${name} out of this picture`,
    drawnFromNobody: "Nobody chosen — add a character to keep their face.",
    castNoneYet: "This book has no characters yet.",
    // Earlier pictures sent for the place, not the people (ADR-0072).
    besideLabel: "Same place as:",
    besideNone: "Nothing chosen — each picture invents its own setting.",
    besideAdd: "Use an earlier picture",
    besideWhy:
      "The setting, light and objects carry over — not the people, the framing or the moment. The description each was drawn from goes with it.",
    besideRemove: (chapter: string) => `Stop using the picture from ${chapter}`,
    besideFull: (n: number) => `${n} at most; a third replaces the oldest.`,
    // Illustrating a whole chapter in one run (ADR-0074).
    illustrateChapter: "Illustrate this whole chapter",
    illustrateWhy:
      "The model reads the chapter, chooses where the pictures go, then draws them one at a time — each in the same world as the last. They arrive placed, and stay drafts until you publish.",
    howManyPictures: "How many pictures",
    illustrateStart: "Illustrate the chapter",
    illustrateStarting: "Starting…",
    illustrateFailed: "Could not start. Try again.",
    runPlanning: "Reading the chapter…",
    // What a run says, chosen from its status by `runLine` (ADR-0085) — never
    // by counting up to a number the run may never reach.
    runDrawing: (at: number, of: number) => `Drawing ${n(at)} of ${n(of)}…`,
    runDrawn: (drawn: number, planned: number) =>
      `${n(drawn)} of ${n(planned)} ${s(planned, "picture", "pictures")} drawn.`,
    // Both numbers and no cause: the shortfall may be the spacing rule dropping
    // spots the model did offer, and neither side may guess which.
    runShort: (drawn: number, planned: number, wanted: number) =>
      `${n(drawn)} of ${n(planned)} ${s(planned, "picture", "pictures")} drawn · ${n(wanted)} asked for.`,
    runNone: "No pictures were drawn.",
    // A run somebody stopped (ADR-0167). What it placed stays.
    runStopped: (drawn: number) =>
      drawn === 0
        ? "Stopped. No pictures were placed."
        : `Stopped. ${n(drawn)} ${s(drawn, "picture", "pictures")} placed.`,
    stopRun: "Stop",
    stoppingRun: "Stopping…",
    stopRunFailed: "Could not stop. Try again.",
    runReason: (reason: string) =>
      ({
        needs_face: "Somebody in this picture has no face yet.",
        text_changed: "The paragraph it was for has been edited since.",
        nothing: "The model found nowhere worth a picture.",
        refused: "The model declined to draw it.",
        budget: "This library's budget is spent.",
        failed: "The model could not be reached.",
        timed_out: "Nothing was heard back.",
      })[reason] ?? "Something went wrong.",
    uploadedNote: "Uploaded",
    // Orphans.
    unplaced: (n: number) =>
      `${n === 1 ? "One picture" : `${n} pictures`} lost ${n === 1 ? "its" : "their"} place`,
    unplacedWhy:
      "The paragraph each one followed has been edited. Move it to a paragraph that is still here, or remove it.",
    followed: (text: string) => `It followed: ${text}`,
    // Counts.
    countInChapter: (n: number) => (n === 1 ? "1 picture" : `${n} pictures`),
    none: "No pictures in this chapter yet.",
    loadFailed: "Pictures could not be loaded.",
    // The portrait half of the same sheet (ADR-0039).
    portraitEmpty:
      "The description is written from the roster and the passages they appear in, and you can edit every word of it before anything is drawn.",
    portraitSuggest: "Suggest how they look",
    portraitPlaceholder: "What does this character look like?",
    // The cover half of the same sheet (ADR-0041).
    coverEmpty:
      "The description is written from the book's blurb, its chapters and its characters, and you can edit every word of it before anything is drawn. Add a character to it and their face comes from their portrait.",
    coverSuggest: "Suggest a cover",
    coverPlaceholder: "What should the cover show?",
    needsFaces: (names: string[]) =>
      `${names.join(", ")} ${s(names.length, "has", "have")} no picture yet, so this cannot be drawn. Give them a portrait on the Characters screen first, or take them out of this picture.`,
  },
  portraits: {
    title: (name: string) => `Portrait of ${name}`,
    add: (name: string) => `Add a portrait of ${name}`,
    change: (name: string) => `Change the portrait of ${name}`,
    none: "No face",
    remove: "Remove",
    removeFailed: "Could not remove that portrait.",
    // The prerequisite, said where somebody is about to be stopped by it.
    castTitle: "Find this book's characters first",
    castWhy:
      "A picture of a scene is drawn from the portraits of the characters it names. That is what keeps them looking like the same people from chapter to chapter, so the book is read for its cast first.",
    castLazy: "A picture that names them will ask for their portrait first.",
    castNotExtracted: "Nobody has read this book for its characters yet.",
    castMissing: (names: string[]) =>
      names.length === 1
        ? `${names[0]} has no portrait yet.`
        : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]} have no portraits yet.`,
    castMissingCount: (missing: number, total: number) =>
      `${n(missing)} of this book's ${n(total)} characters have no portrait yet.`,
    castProgress: (done: number, total: number) =>
      `${n(done)} of ${n(total)} characters have a face.`,
    castStale: (n_: number) =>
      `${n(n_)} ${n_ === 1 ? "chapter has" : "chapters have"} changed since the cast was read. New characters may be missing.`,
    goToCast: "Go to the characters",
    // Several faces at once (ADR-0049).
    batchStart: "Draw several",
    batchPick:
      "Tick who to draw. Each gets a description written from the book and a square picture drawn from it, replacing any face they have. You can redraw any of them afterwards.",
    batchAll: "All",
    batchNone: "None",
    batchWithoutFaces: "Those without a face",
    batchCancel: "Cancel",
    batchDraw: (count: number) => `Draw ${n(count)} ${count === 1 ? "portrait" : "portraits"}`,
    batchChoose: (name: string) => `Draw a portrait of ${name}`,
    batchDrawing: "Drawing…",
    batchQueued: (count: number) =>
      `${n(count)} ${count === 1 ? "portrait is" : "portraits are"} being drawn. You can leave this screen.`,
    batchQueueFailed: "Could not start drawing those portraits.",
    batchReasons: {
      refused: "The model declined to draw them. Change their description and try again.",
      budget: "The library's budget is spent.",
      failed: "The picture service kept failing. Try again.",
      timed_out: "This took too long and was abandoned. Try again.",
    } as Record<string, string>,
    batchFailedFallback: "This portrait could not be drawn.",
  },
  // A portrait brought to life (requirement 0059, ADR-0127).
  live: {
    start: "Make live portraits",
    pick: "Tick whose portrait to bring to life: the head turns, the chest breathes, the eyes blink. Only characters with a portrait can be chosen. It is made from the portrait, so a new portrait removes it.",
    all: "All with a portrait",
    without: "Those without one",
    none: "None",
    cancel: "Cancel",
    make: (count: number) => `Make ${n(count)} live ${count === 1 ? "portrait" : "portraits"}`,
    choose: (name: string) => `Make a live portrait of ${name}`,
    making: "Bringing to life…",
    queued: (count: number) =>
      `${n(count)} live ${count === 1 ? "portrait is" : "portraits are"} being made. The first can take a few minutes while a machine starts. You can leave this screen.`,
    queueFailed: "Could not start making those live portraits.",
    badge: "Live",
    badgeLabel: (name: string) => `${name} has a live portrait`,
    heading: "Live portrait",
    note: "Move the pointer over it. Readers see it once you publish.",
    reasons: {
      refused: "This portrait could not be brought to life.",
      budget: "The library's budget is spent.",
      failed: "The machine kept failing. Try again.",
      timed_out: "This took too long and was abandoned. Try again.",
      expired: "It waited too long for a machine. Try again.",
      portrait_changed: "The portrait changed while this was being made. Try again.",
    } as Record<string, string>,
    failedFallback: "This live portrait could not be made.",
  },
  cover: {
    title: "Book cover",
    note: "One picture for the whole book. It is what a reader sees in the library. A new cover is a draft until you publish, like everything else readers are served. Uploading is free; drawing one costs about the same as any other picture.",
    none: "No cover yet",
    add: "Add a cover",
    change: "Change the cover",
    remove: "Remove",
    removeQuestion: "Remove this cover?",
    removeFailed: "Could not remove the cover.",
    missingFaces: (names: string[]) =>
      `${names.join(", ")} ${s(names.length, "has", "have")} no picture yet, so ${s(names.length, "they cannot", "they cannot")} be named on the cover.`,
    toCharacters: "Characters",
  },
  crop: {
    // On a picture, in the sheet or in the chapter.
    crop: "Crop",
    title: "Crop the picture",
    hint: "Drag the frame to move it, or a corner to resize it.",
    apply: "Apply crop",
    applying: "Cropping…",
    cancel: "Cancel",
    // The shape chip that leaves the frame's proportions to the author.
    free: "Free",
    failed: "Could not crop that picture.",
    // Where the frame's shape is not a choice.
    lockedSquare: "A portrait is square, so every scene drawn from it is too.",
    lockedCover: "A cover is upright, so the library's rows line up.",
  },
  edition: {
    heading: "Readers' edition",
    published: (num: number, date: string) => `Edition ${n(num)} · published ${date}`,
    notPublished: "Not published yet. Readers see nothing until you publish.",
    upToDate: "Readers have everything you've written.",
    charactersChanged: "The characters changed since the last edition.",
    coverChanged: "The cover changed since the last edition.",
    speakersChanged: (lines: number) =>
      `Who says ${n(lines)} ${s(lines, "line", "lines")} changed since the last edition.`,
    speakersLost: (lines: number) =>
      `${n(lines)} ${s(lines, "line", "lines")} would lose ${s(lines, "its speaker", "their speakers")}. Press Update under “Who says each line” before publishing.`,
    /** A chapter's pictures, in the change row beside "Text edited". */
    picturesAdded: (c: number) => `${n(c)} ${s(c, "picture", "pictures")} added`,
    picturesRemoved: (c: number) => `${n(c)} ${s(c, "picture", "pictures")} removed`,
    picturesEdited: (c: number) => `${n(c)} ${s(c, "picture", "pictures")} edited`,
    changes: (c: number) => `${n(c)} ${s(c, "change", "changes")} readers don't have yet`,
    added: "New chapter",
    removed: "Removed",
    renamed: "Renamed",
    moved: "Moved",
    text: "Text edited",
    audio: "New audio",
    noAudio: "No audio",
    audioOutdated: "Audio is from earlier text",
    warning: (c: number) =>
      `${n(c)} ${s(c, "chapter", "chapters")} would go out without audio that matches the text.`,
    note: "Note for readers (optional)",
    notePlaceholder: "What changed in this edition",
    publish: (num: number) => `Publish edition ${n(num)}`,
    publishing: "Publishing…",
    publishFailed: "Could not publish.",
    readerLine: (num: number, date: string) => `Edition ${n(num)} · ${date}`,
    /** Who may read the edition (ADR-0152). */
    whoCanRead: "Who can read",
    everyone: "Everyone",
    onlyYou: "Only you",
    everyoneNote: "Listed in Browse for every signed-in reader.",
    onlyYouNote: "Only you can read it, in the app and here. It is not listed anywhere.",
    visibilityFailed: "Could not change who can read it. Try again.",
    /** Shared with the library's chosen groups (ADR-0153). */
    groups: "Groups",
    groupsNote: "Only the people in the groups you choose can read it. It is not listed anywhere.",
    noGroupChosen: "Choose at least one group, or nobody but you can read it.",
    noGroupsYet: "You have no groups yet.",
    manageGroups: "Manage groups",
  },
  library: {
    title: "Your library",
    pickUp: "Pick up where you left off",
    /** The resume card's menu: out of the row, the place kept (owner, 10-22). */
    pickUpMenu: "More for this book",
    removeFromPickUp: "Remove from Pick up where you left off",
    removedFromPickUp: (title: string) => `“${title}” removed. Your place is kept.`,
    undo: "Undo",
    percentThrough: (p: number, title: string) => `${n(p)}% through ${title}`,
    /** A tile's progress bar, which has no visible number beside it (ADR-0061). */
    chaptersFinished: (done: number, total: number) =>
      `${n(done)} of ${n(total)} chapters finished`,
    /** The seal on a finished book's tile (owner, 10-22): the word and the month. */
    finishedSeal: (month: string) => `Finished · ${month}`,
    loadingBooks: "Loading books",
    noBooks: "No books have been published yet.",
    draft: "Draft",
    /** A published book only its owner can read (ADR-0152). */
    onlyYou: "Only you",
    /** The owner's mark on a book shared with groups (ADR-0153). */
    shared: "Shared",
    /** A reader's mark on a book shared with them, and the list of them. */
    sharedWithYou: "Shared with you",
    /** The shelf is empty because it is new, not because nothing exists. */
    emptyTitle: "Your library is empty",
    emptyBody: "Books you save, and books you start listening to, are kept here.",
    browseBooks: "Browse books",
    save: "Add to your library",
    saved: "In your library",
    remove: "Remove from your library",
    saveFailed: "That did not save. Try again.",
  },
  browse: {
    title: "Browse",
    allBooks: "All books",
  },
  // Tags (requirement 0119 T3).
  tag: {
    filter: "Filter by tag",
    all: "All",
    nothing: "No book here has this tag.",
    tags: "Tags",
    // The studio.
    note: "Words readers filter Browse by. Every library shares one list of tags.",
    none: "No tags yet.",
    remove: (name: string) => `Remove ${name}`,
    search: "Find or make a tag",
    create: (name: string) => `New tag “${name}”`,
    saveFailed: "Could not save the tags.",
    searchFailed: "Could not search the tags.",
    // The admin panel.
    link: "Tags",
    title: "Tags",
    what: "Every tag, on every library's books. A rename or merge changes every book that carries it.",
    empty: "No tags yet.",
    listFailed: "Could not load the tags.",
    books: (count: number) => `${n(count)} ${s(count, "book", "books")}`,
    rename: "Rename",
    merge: "Merge into…",
    mergeInto: (name: string) => `Merge into ${name}`,
    mergeCancel: "Cancel",
    delete: "Delete",
    deleteQuestion: (name: string) => `Delete “${name}” from every book?`,
  },
  writer: {
    translatedBy: "Translated by",
    books: "Books",
    notFound: "There is no writer here, or none of their books are yours to read.",
    // The studio (0118 W2).
    writers: "Writers",
    writersNote:
      "Who wrote the book, and who translated it. Readers tap a name to see the writer's other books.",
    none: "No writers yet.",
    author: "Author",
    translator: "Translator",
    moveUp: "Move up",
    moveDown: "Move down",
    remove: "Remove",
    edit: "Edit",
    add: "Add a writer",
    addAs: "Add as",
    search: "Search by name",
    create: (name: string) => `New writer “${name}”`,
    sageleafNote: "for a machine translation",
    saveFailed: "Could not save the writers.",
    searchFailed: "Could not search the writers.",
    editTitle: "Writer",
    name: "Name",
    dates: "Dates",
    datesHint: "For example: c. 50 – c. 135",
    bio: "About",
    save: "Save",
    saved: "Saved",
    editFailed: "Could not save the writer.",
    shared: "Other libraries' books name this writer too, so only a SageLeaf admin can edit it.",
    backToBook: "Back to the book",
    // The portrait (0118 W3).
    portrait: "Portrait",
    uploadPortrait: "Upload",
    drawPortrait: "Draw",
    drawNote: "Drawn from the name, dates and about, in the house style — a few cents.",
    portraitFailed: "Could not set the portrait.",
  },
  plus: {
    /** The lock on a tile and the page's line: a Plus book this reader has
     *  no Plus for (ADR-0193). */
    badge: "Plus",
    /** A tile's badge when reading needs only signing in (ADR-0197). */
    signInBadge: "Sign in",
    /** A tile's badge, on the visitor's shelf, for a book that opens now. */
    openBadge: "Read now",
    /** The face's counts on a visitor's tile (ADR-0197). */
    narratedHours: (hours: number) => `Narrated · ${n(hours)} h`,
    faces: (c: number) => `${n(c)} ${s(c, "face", "faces")}`,
    pictures: (c: number) => `${n(c)} ${s(c, "picture", "pictures")}`,
    /** Get Plus for a visitor: Plus is held by an account, so sign in first. */
    signInForPlus: "Sign in to get Plus",
    /** A book that opens for anyone signed in (ADR-0197's sign_in). */
    signInToRead: "Sign in to read",
    offerTitle: "This book is part of Plus",
    offerBody: "With Plus:",
    /**
     * What Plus gives, said of the book the reader tapped: a narration line
     * only when this book has audio, never a promise that every book is
     * narrated (most are not). No book (a chapter opened by link) gets the
     * general list. The server says nothing yet about pictures or portraits,
     * so that line holds for any book.
     */
    benefits: (book?: { narrated: boolean; voices: boolean }): string[] => [
      "Every book in the library",
      ...(!book
        ? ["Narration on narrated books"]
        : book.narrated
          ? [
              book.voices
                ? "Narrated, with a voice for each character"
                : "Narrated, to listen as you read",
            ]
          : []),
      "Pictures and character faces where a book has them",
      "Ask about any line",
    ],
    /** No price on any button, in either app (store rules; requirement 0125). */
    getPlus: "Get Plus",
    goodNews: "Good news — you've got early access to Plus, free while SageLeaf is in beta.",
    /** The price lives in the question, never on a button. */
    question: "When Plus becomes paid, would $7.99 a month be worth it to you?",
    choices: {
      yes: "Yes",
      not_at_that_price: "Not at that price",
      not_sure: "Not sure",
    } as Record<string, string>,
    noteLabel: "What would make it worth it? (optional)",
    pickToContinue: "Pick an answer to continue.",
    continue: "Continue to the full library",
    terms:
      "Beta features and access may change; we'll give at least 14 days' notice before anything you use becomes paid.",
  },
  book: {
    backToStudio: "Studio",
    backToLibrary: "Library",
    viewAsReader: "View as reader",
    openInStudio: "Open in studio",
    share: "Share",
    linkCopied: "Link copied",
    noChapters: "No chapters yet. Paste the first one below.",
    addHeading: "Add a chapter",
    chapterTitle: "Chapter title",
    pasteText: "Paste the chapter text",
    chapterText: "Chapter text",
    addChapter: "Add chapter",
    addFailed: "Could not add the chapter.",
    tools: "Book tools",
    deleteNote: "Deleting a book hides it and everything in it. There is no undo here.",
    deleteBook: "Delete book",
    deleteBookQuestion: (c: number) =>
      `Delete this book and its ${n(c)} ${s(c, "chapter", "chapters")}?`,
    deleteFailed: "Could not delete the book.",
    revision: (r: number) => ` · revision ${n(r)}`,
    renameChapter: "Rename chapter",
    moveChapter: "Drag to reorder, or use the arrow keys",
    reorderFailed: "Could not save the new order.",
    deleteChapter: "Delete chapter",
    deleteChapterQuestion: "Delete this chapter?",
    bookNoun: "book",
    chapterNoun: "chapter",
    language: "Language",
    languageNote:
      "What language this book is written in. It is detected from the first chapter, and it decides what language the AI writes its characters, descriptions and picture briefs in.",
    languageUnset: "Not chosen — go by the text",
    languageFailed: "Could not change the language.",
    story: "Kind of book",
    storyNote:
      "A story, or letters, essays and the like. Answers read a story as plot and can keep its spoilers back; anything else is read as an argument, and always asked about openly.",
    storyYes: "Story",
    storyNo: "Not a story",
    storyFailed: "Could not change the kind of book.",
    pictureMedium: "Picture medium",
    pictureMediumNote:
      "A few words every picture and the cover of this book are drawn in. Changing it is free and redraws nothing: it applies to pictures described from now on. Portraits keep their own look.",
    pictureMediumPlaceholder: "Chosen with the first picture",
    pictureMediumClear: "Choose with the next picture",
    pictureMediumTooLong: (max: number) => `Keep it under ${n(max)} characters.`,
    pictureMediumFailed: "Could not save the picture medium.",
    editList: "Edit list",
    doneEditing: "Done",
    stateNarrating: "Narrating…",
    stateNotNarrated: "Not narrated",
    stateAudioOutdated: "Audio behind the text",
    stateNarrated: "Narrated",
    stateNarrationFailed: "Narration failed",
    stateMissing: (c: number) => `${n(c)} ${s(c, "passage", "passages")} missing`,
    publishLineUnpublished: "Not in the published edition.",
    publishLineOutdated: "Readers have an earlier version of this chapter.",
    publishLineLink: "Publish",
    summaryChapters: (c: number) => `${n(c)} ${s(c, "chapter", "chapters")}`,
    length: (minutes: number) =>
      minutes >= 60
        ? `${n(Math.floor(minutes / 60))} h ${n(minutes % 60)} min`
        : `${n(minutes)} min`,
    minutesLeft: (minutes: number) => `${n(minutes)} min left`,
    startListening: "Start listening",
    startReading: "Start reading",
    /** Between two facts on one line. Persian gets its comma: its fonts draw
     *  a middle dot like the digit zero. */
    sep: " · ",
    continueListening: "Continue",
    listenAgain: "Listen again",
    readAgain: "Read again",
    chaptersHeading: "Chapters",
    finished: "Finished",
    more: "More",
    less: "Less",
    summaryNotNarrated: (c: number) => `${n(c)} not narrated`,
    summaryOutdated: (c: number) => `audio behind the text in ${n(c)}`,
    summaryNarrating: (c: number) => `narrating ${n(c)}`,
    summaryToPublish: (c: number) => `${n(c)} ${s(c, "change", "changes")} to publish`,
    summaryAllDone: "narrated and published",
  },
  title: {
    edit: (noun: string) => `Edit ${noun}`,
    rename: (noun: string) => `Rename this ${noun}`,
    renameFailed: "Could not rename that.",
  },
  file: {
    tooLarge: "That file is too large to be a chapter.",
    noText: "That file has no text in it.",
    tooLong: (c: number, max: number) =>
      `That is ${n(c)} characters. A chapter can hold ${n(max)} — split it first.`,
    loaded: (name: string, c: number) => `Loaded ${name} · ${n(c)} characters`,
    loadedAs: (name: string, encoding: string, c: number) =>
      `Loaded ${name} as ${encoding} · ${n(c)} characters`,
    choose: "Choose a file",
    orDrop: "or drop a .txt, .md or .epub file here",
    reading: (name: string) => `Reading ${name}…`,
    readFailed: "Could not read that EPUB.",
    found: (name: string, c: number) =>
      `${name} · ${n(c)} ${s(c, "chapter", "chapters")}. Untick any you do not want.`,
    untitled: (position: number) => `Chapter ${n(position)}`,
    tooLongToAdd: (max: number) => `Too long to add — a chapter holds at most ${n(max)} characters`,
    addChapters: (c: number) => `Add ${n(c)} ${s(c, "chapter", "chapters")}`,
    addFailed: "Could not add the chapters.",
    another: "Choose another file",
  },
  voice: {
    label: "Voice · ",
    theDefault: " (the default)",
    /** A voice designed for this book's narrator (ADR-0157). */
    designed: "Designed for this book",
    /**
     * The label only. The sentence itself is put in a `<bdi>` by the picker:
     * it is always English (narration.SampleSentence), and in a Persian line a
     * Latin run's quotes swap ends without one.
     */
    everySample: "Every sample reads:",
    loadFailed: "Could not load the voices.",
    playSample: (name: string) => `Play a sample of ${name}`,
    modelLabel: "Speech model",
    /** Model names in this language; a model not listed keeps the server's label. */
    models: {
      "gemini-3.8-flash-tts": "Gemini 3.8 Flash",
      "gemini-3.8-flash-lite-tts": "Gemini 3.8 Flash-Lite",
      // Retired (ADR-0132); kept because existing audio still names them.
      "gemini-3.1-flash-tts-preview": "Gemini 3.1 Flash",
      "gemini-2.5-pro-preview-tts": "Gemini 2.5 Pro",
      "tts-rt-v2": "Soniox",
      eleven_v4: "ElevenLabs v4",
      eleven_v4_turbo: "ElevenLabs v4 Turbo",
      // Retired (ADR-0147); kept because existing audio still names it.
      eleven_v3: "ElevenLabs v3",
      "qwen3-tts-1.7b": "Qwen3-TTS",
      "kokoro-82m": "Kokoro",
    } as Record<string, string>,
    fixedVoice:
      "The one voice this model speaks in. Choosing this model is the choice; there is nothing to audition.",
    directionLabel: "How it should be read",
    directionPlaceholder: "Unhurried, like an audiobook narrator",
    directionNote:
      "Sent to the narrator as direction, never read aloud. Changing it re-records the chapters you narrate next; what is already recorded stays as it is.",
    directionTooLong: (max: number) => `Keep it under ${n(max)} characters.`,
    directionClear: "No direction",
    /** One tap to start from, because typing a sentence on a phone is the cost. */
    directionSuggestions: [
      "Unhurried, like an audiobook narrator",
      "Warm and close, as if reading to one person",
      "Brisk and clear",
      "Grave and deliberate",
    ] as string[],
    partSizeLabel: "Part size",
    partSizeChip: (chars: number) => n(chars),
    partSizeOption: (chars: number, seconds: number) =>
      `${n(chars)} characters · about ${n(seconds)} s`,
    partSizeDefault: " (default)",
    partSizeCapped: (max: number) =>
      `On this model parts are at most ${n(max)} characters, so none is cut off.`,
    readLabel: "Record each part from the sentence before it, then cut that sentence out",
    readNote: "Smoother joins between parts. Costs a little more, and works in fast mode only.",
  },
  /** A book's cast of voices (ADR-0157, requirement 0090). */
  voices: {
    open: "Voices for each character",
    openNote: "Design a voice for the narrator and each character who speaks.",
    note: "Each voice is designed from its description and heard reading its own lines from the book. Choose one of two; you can cast again later.",
    notOffered: "Voices for each character are offered on English books narrated in ElevenLabs v4.",
    loadFailed: "Could not load the cast.",
    narrator: "Narrator",
    lines: (count: number) => `${n(count)} ${s(count, "line", "lines")}`,
    chosen: "Chosen voice",
    playChosen: "Play the chosen voice",
    noVoice: "No voice yet: the narrator reads these lines.",
    narratorStock: "No designed voice yet: the book's voice reads.",
    description: "Voice description",
    reads: "Reads:",
    design: (amount: string) => `Design two voices · ${amount}`,
    designAgain: (amount: string) => `Design two more · ${amount}`,
    designing: "Designing…",
    candidate: (i: number) => `Voice ${n(i)}`,
    playCandidate: (i: number) => `Play voice ${n(i)}`,
    choose: "Choose",
    choosing: "Saving…",
    failed: "That did not work. Try again.",
    useExisting: "Use an existing voice",
    hideExisting: "Hide existing voices",
    existingNote: "A voice already on the voice service's account. Taking one costs nothing.",
    sample: (amount: string) => `Hear it read these lines · ${amount}`,
    sampling: "Reading…",
    playsSample: "Plays this voice reading the role's lines.",
    playsPreview: "Plays the maker's own preview, not this role's lines.",
    slots: (used: number, limit: number) => `Voice slots ${n(used)} / ${n(limit)}`,
    noAccountVoices: "The account holds no voices of its own yet.",
    voiceID: "Voice id",
    voiceLabel: "Name (optional)",
    useVoice: "Use this voice",
    using: "Checking…",
    findLibrary: "Find in library",
    hideLibrary: "Hide library voices",
    libraryNote:
      "Voices other people made, found from the description. Previews are free, and so is choosing one: it is added to the voice service's account — no voice slot, but one of the month's voice adds.",
    libraryAdds: (used: number, limit: number) => `Voice adds this month ${n(used)} / ${n(limit)}`,
    libraryWithdraw:
      "A library voice belongs to its maker, who can withdraw it after giving notice.",
    libraryNotice: (days: number) => `Notice ${n(days)} days`,
    libraryInAccount: "In the account",
    libraryHQ: "HQ",
    libraryHQTitle: "High quality, by the voice service's own mark",
    libraryRelaxed: "Few exact matches, so the search was widened.",
    accent: "Accent",
    accentNote:
      "The accent this book's voices speak in. A description that names its own keeps it.",
    accentFailed: "Could not change the accent.",
    accentName: (accent: string) =>
      ({
        american: "American",
        british: "British",
        australian: "Australian",
        irish: "Irish",
        scottish: "Scottish",
      })[accent] ?? accent,
    libraryNone: "Nothing in the library matches. Try other words in the description.",
    librarySearchAgain: "Search with this description",
    libraryPreview: (name: string) => `Play ${name}`,
    libraryChosen: "From the voice library: its maker can withdraw it.",
    chosenNamed: (label: string) => `Chosen voice · ${label}`,
    needsVoice: (count: number) => `Needs a voice · ${n(count)}`,
    hasVoice: (count: number) => `Has a voice · ${n(count)}`,
    extrasTitle: "Extra voices",
    extrasNote:
      "For people the book does not name as characters — an envoy, a guard, an old elder. Preparing a chapter for voices picks one of these for each of their lines, and a kind is listed here once it has one. A kind with no voice is read by the narrator.",
    extra: {
      older_man: "Older man",
      younger_man: "Younger man",
      older_woman: "Older woman",
      younger_woman: "Younger woman",
      child: "Child",
    } as Record<string, string>,
  },
  script: {
    multiSpeakerLabel: "A voice for every character",
    multiSpeakerNote:
      "Each character's lines are read in their own voice, from a script prepared chapter by chapter. English books on ElevenLabs v4.",
    title: "Prepare for voices",
    note: "Writes a short delivery cue before every line a character speaks and a pause at each story beat, in one pass over the chapter. Your text stays as it is: only the narrator and the voices hear the marks.",
    prepare: "Prepare for voices",
    notStarted: "Not prepared yet",
    preparing: (done: number, total: number) =>
      `Preparing… ${n(done)} of ${n(total)} ${s(total, "paragraph", "paragraphs")}`,
    summary: (cues: number, pauses: number) =>
      `${n(cues)} ${s(cues, "cue", "cues")}, ${n(pauses)} ${s(pauses, "pause", "pauses")}`,
    prepareRest: "Prepare the rest",
    retry: "Try the failed paragraphs again",
    failed: (k: number) => `${n(k)} ${s(k, "paragraph", "paragraphs")} could not be prepared:`,
    notes: "Some marks could not be used:",
    clear: "Remove the script",
    narrator: "Narrator",
    someoneElse: "Someone else",
    notSpeech: "Not speech",
    unknown: "Not attributed yet",
    cueLabel: "How the line is spoken",
    cuePlaceholder: "add a cue",
    pause: "pause",
    longPause: "long pause",
    addPause: "Pause after this paragraph",
    pauseCycle: "Tap to change the pause",
    speakerLabel: "Who says this line",
    edited: "edited",
    saveFailed: "That change was not saved.",
    extraLabel: "Which extra voice reads it",
    extraNote: "Changes every line of this speaker in the chapter.",
    extraUncast: "No voice cast for this kind yet: the narrator reads it.",
    nameLabel: "Who is speaking",
    namePlaceholder: "name the speaker",
    unnamed: (k: number) =>
      `${n(k)} ${s(k, "line", "lines")} by someone else with no speaker named`,
  },
  narration: {
    backToBook: "Book",
    read: "Preview as reader",
    textChanged: "The text or its corrections have changed since this was narrated.",
    performedChanged:
      "The text, its script for voices or a character's voice has changed since this was narrated.",
    performedWith: (voices: string) =>
      `Narrated with a voice for every character: ${voices}. Everyone else is read by the narrator.`,
    performedLines: (name: string, lines: number) =>
      `${name} (${n(lines)} ${s(lines, "line", "lines")})`,
    performedNoVoices:
      "Narrated from its script for voices; no character with lines here has a voice yet, so the narrator reads them all.",
    onlyRerecorded: (k: number, total: number) =>
      ` Only ${n(k)} of ${n(total)} ${s(total, "part", "parts")} would be re-recorded.`,
    levelsChanged:
      "The voices can be evened out: narrating again re-levels the recorded parts, at no cost.",
    relevelled: (c: number) => `${n(c)} ${s(c, "part", "parts")} re-levelled, free`,
    parts: (c: number) => `${n(c)} ${s(c, "part", "parts")}`,
    alreadyNarrated: (c: number) => `${n(c)} already narrated`,
    voiceChanged: (from: string, to: string, c: number) =>
      `This chapter is narrated in ${from}. Narrating it in ${to} re-records all ${n(c)} ${s(c, "part", "parts")} at full price.`,
    modelChanged: (c: number) =>
      `This chapter was narrated with a different speech model. Re-narrating it re-records all ${n(c)} ${s(c, "part", "parts")} at full price.`,
    economy: "Economy — half price",
    whatIsEconomy: "What is economy mode?",
    economyExplainer:
      "The same audio for half the price, generated when the provider gets to it rather than while you wait — measured at about four minutes for a short chapter and over an hour for a long one. You can close the page; it carries on.",
    narrate: "Narrate",
    renarrate: "Re-narrate",
    // A fresh take (ADR-0162): every part recorded anew, nothing reused.
    recordAgain: "Record again",
    recordAgainTitle: (c: number) =>
      `Record all ${n(c)} ${s(c, "part", "parts")} anew at full price, with the same text, script and voices.`,
    settings: "Narration settings",
    settingsNote:
      "Voice, speech model and part size belong to the book: every chapter is narrated with them.",
    settingsSummary: (voice: string, model: string, chars: number) =>
      `${voice} · ${model} · ${n(chars)}-character parts`,
    prepareNotStarted: "Not prepared",
    prepareFirst: "Prepare the text first",
    preparingFirst: (done: number, total: number) =>
      `Preparing the text, ${n(done)} of ${n(total)} ${s(total, "piece", "pieces")}, then narrating…`,
    narrateAll: "Narrate all",
    narrateAllEstimate: (chapters: number, cost: string) =>
      `${n(chapters)} ${s(chapters, "chapter needs", "chapters need")} narrating · about ${cost}`,
    narrateAllNothing: "Every chapter is narrated.",
    narrateAllStarted: (c: number) => `Narrating ${n(c)} ${s(c, "chapter", "chapters")}…`,
    narrateAllFailed: "Could not start narrating.",
    problemsLine: (passages: number, chapters: number) =>
      `Narration · ${n(passages)} ${s(passages, "passage", "passages")} missing in ${n(chapters)} ${s(chapters, "chapter", "chapters")}`,
    problemsNone: "Every narrated passage has its audio.",
    problemsNote:
      "Passages the narration has no audio for. The rest of each chapter plays; fix these and narrate the chapter again, and only they are recorded.",
    problemsChapterMissing: (c: number) => `${n(c)} ${s(c, "passage", "passages")} missing`,
    problemsChapterFailed: "No part of this chapter has audio",
    problemCause: {
      unsaid_run: "The check did not hear the highlighted words in the recording",
      stopped_early: "The recording stopped before the end of the passage",
      lead_in_miss: "The join with the passage before did not come out cleanly",
      refused: "The speech model refused to read this passage",
      account: "The speech provider account could not serve this: check its key or balance",
      provider_error: "The speech provider failed",
      timed_out: "It took too long and was given up on",
      unknown: "It failed",
      cue_spoken: "The recording said a direction aloud instead of following it",
      voice_gone: "The voice cast for this part is no longer at the voice service",
      extra_speech: "The recording says words the text does not have",
    } as Record<string, string>,
    problemSkippedSomewhere: "The check did not hear some words of this passage in the recording",
    // Only for causes that are not retryable (ADR-0120). unsaid_run is the
    // check's verdict, not a fact about the audio: it names both cases and
    // never invites listening, since a rejected part's audio is not kept.
    problemNoRetry: {
      unsaid_run:
        "Narrating again gives the same recording. Words the narrator cannot read, such as another script or symbols, need editing. If they are names, the check may have misheard them: a pronunciation helps only a name you have heard said wrongly.",
      refused: "Trying again gives the same result: edit the text.",
      voice_gone: "Trying again finds the voice gone again: cast the role another voice.",
    } as Record<string, string>,
    problemShowInText: "Show in text",
    problemTryAgain: (c: number) => (c === 1 ? "Try again" : `Try the ${n(c)} again`),
    problemDetails: "Details",
    problemsFailed: "Could not load the passages that need attention.",
    narrateAllTitle: "Narrate these chapters?",
    narrateAllNotNarrated: (c: number) => `${n(c)} not narrated yet`,
    narrateAllTextChanged: (c: number) => `${n(c)} with changed text`,
    narrateAllRefinish: (c: number) => `${n(c)} to re-level, free`,
    narrateAllRevoiced: (c: number) => `${n(c)} with the same text, voiced differently`,
    narrateAllRecordedOn: (c: number, model: string, voice: string) =>
      `${n(c)} recorded on ${model} · ${voice}`,
    narrateAllRecordedWith: (c: number, settings: string) =>
      `${n(c)} recorded with a different ${settings}`,
    narrateAllSetting: {
      model: "speech model",
      voice: "voice",
      part_size: "part size",
      read_context: "lead-in",
      direction: "direction",
      language: "language",
    },
    narrateAllSettingSeparator: " and ",
    narrateAllRedoNote: (model: string, voice: string) =>
      `Ticked groups are re-recorded in full on the book's settings, ${model} · ${voice}.`,
    narrateAllConfirm: (c: number) => `Narrate ${n(c)} ${s(c, "chapter", "chapters")} · about`,
    narrateAllCancel: "Cancel",
    narrateAllRunning: (done: number, planned: number) =>
      `Starting chapters, ${n(done)} of ${n(planned)}…`,
    narrateAllDone: (started: number, notStarted: number) =>
      `Started ${n(started)} ${s(started, "chapter", "chapters")}.` +
      (notStarted > 0 ? ` ${n(notStarted)} could not be started.` : ""),
    narrateAllStoppedBudget: (started: number, planned: number) =>
      `Stopped after ${n(started)} of ${n(planned)} chapters: the budget ran out.`,
    narrateAllStoppedNoBudget: "Stopped: this library has no spending limit set.",
    narrateAllRunFailed: (started: number, planned: number) =>
      `Stopped after ${n(started)} of ${n(planned)} chapters. Press Narrate all to carry on.`,
    prepareSummary: (marks: number) => `${n(marks)} ${s(marks, "mark", "marks")} added`,
    prepareTitle: "Prepare the text for narration",
    prepareNote:
      "Adds kasre-ye ezafe and commas where they are spoken but not written, a piece at a time. Nothing else may change, and your text stays as it is: only the narrator hears the marks.",
    prepare: "Prepare text",
    preparing: (done: number, total: number) =>
      `Preparing… ${n(done)} of ${n(total)} ${s(total, "piece", "pieces")}`,
    prepared: (marks: number, done: number, total: number) =>
      `${n(marks)} ${s(marks, "mark", "marks")} added in ${n(done)} of ${n(total)} ${s(total, "piece", "pieces")}. Narrate to hear them.`,
    prepareRest: "Prepare the rest",
    prepareRetry: "Try the failed pieces again",
    prepareFailed: (k: number) => `${n(k)} ${s(k, "piece", "pieces")} could not be prepared:`,
    prepareNotes: "Some of the text was left unmarked:",
    reviewMarks: "Review marks",
    hideMarks: "Hide marks",
    reviewNote: "Tap a highlighted word to remove the mark added to it.",
    clearPreparation: "Remove preparation",
    startFailed: "Could not start narration.",
    narrating: (done: number, total: number) => `Narrating ${n(done)} of ${n(total)}…`,
    economyWaiting: " Queued at the economy rate — this can take an hour or more.",
    stop: "Stop",
    stopFailed: "Could not stop this narration.",
    prepareStillRunning:
      "The text is still being prepared. Nothing was narrated — open the preparation to see where it stopped, then try again.",
    settingChangeFailed: "That setting was not saved.",
    cancelled: "Stopped. Parts already narrated are kept — Narrate picks up the rest.",
    finished: "Finished",
    finishedPartial: "Finished with some passages missing",
    partsFailed: (c: number) =>
      `${n(c)} ${s(c, "passage has", "passages have")} no audio. The rest of the chapter plays.`,
    failed: (reason?: string) => `Narration failed${reason ? `: ${reason}` : "."} Try again.`,
    noAudio: "This chapter has no audio yet.",
    audioOutdated:
      "This is the current text. The audio is of an earlier version — narrate again to hear this one.",
    playEarlierAudio: "Play the earlier audio",
    playingEarlier: "Playing the earlier version's audio, with its text.",
    showCurrentText: "Show the current text",
  },
  fixes: {
    heading: "Corrections",
    open: (c: number) => `${n(c)} ${s(c, "correction", "corrections")}`,
    detached: (c: number) =>
      `${c === 1 ? "One correction no longer matches the text and is being skipped." : `${n(c)} corrections no longer match the text and are being skipped.`} Long-press the word again to re-anchor, or delete ${s(c, "it", "them")}.`,
    deleteFor: (word: string) => `Delete correction for ${word}`,
    deleteFailed: "That correction was not deleted; it is still in force.",
    scopes: {
      skipped: "skipped",
      // A correction a narrower one has taken over (ADR-0036). It is still
      // saved and still reaches other chapters; here it changes nothing.
      overridden: "overridden",
      occurrence: "one word",
      chapter: "chapter",
      book: "book",
      org: "library",
    },
  },
  search: {
    heading: "Search inside this book",
    on: "Readers' questions are answered from everything they have read so far, with quotations they can tap to go to. Nothing ahead of them is searched.",
    off: "Readers can ask questions, answered from the page they are on. Turn this on to answer from everything they have read so far, with quotations.",
    working: "Working…",
    turnOn: "Turn on",
    turnOff: "Turn off",
    indexed: (i: number, total: number) => `${n(i)} of ${n(total)} chapters searchable`,
    stale: (c: number, cost: string) =>
      `${n(c)} ${s(c, "chapter is", "chapters are")} out of date — update for ${cost}`,
    estimate: (cost: string) => `Indexing costs about ${cost}, once.`,
    pending: "Indexing — this takes a minute.",
    changeFailed: "Could not change the search setting.",
  },
  studio: {
    title: "Studio",
    forbidden:
      "Making books is not switched on for this account. Everything else is: the library reads, plays, and answers questions about any book you can open.",
    backToLibrary: "Back to the library",
    newBookTitle: "New book title",
    newBookLanguage: "What language is it written in?",
    newBook: "New book",
    create: "Create",
    createFailed: "Could not create the book.",
    empty: "No books yet. Start one, then add its chapters.",
    publish: "Publish",
    unpublish: "Unpublish",
    /** The library's groups page (ADR-0153). */
    groups: "Groups",
    /** The book's tools, each a row on the book page and a screen of its own. */
    tools: {
      publish: "Publish",
      characters: "Characters",
      cover: "Cover",
      settings: "Settings",
      narration: "Needs attention",
      voices: "Voices",
    } as Record<string, string>,
    toolCoverSet: "The book has a cover",
    toolCoverNone: "No cover yet",
    toolPublishNone: "Not published yet",
    toolPublishEdition: (edition: number, changes: number) =>
      changes === 0
        ? `Edition ${n(edition)} · up to date`
        : `Edition ${n(edition)} · ${n(changes)} ${s(changes, "change", "changes")} to publish`,
    toolPublishFirst: (changes: number) =>
      `${n(changes)} ${s(changes, "chapter", "chapters")} to publish`,
    toolCharactersNone: "Not extracted yet",
    toolCharactersCount: (c: number) => `${n(c)} ${s(c, "character", "characters")}`,
    toolSettingsSearchOn: "search on",
    toolSettingsSearchOff: "search off",
    toolCharactersStale: (c: number) => `${n(c)} ${s(c, "chapter", "chapters")} changed since`,
    toolSearchStale: (c: number) => `${n(c)} ${s(c, "chapter", "chapters")} out of date`,
  },
  groups: {
    /** A library's groups of people, that books can be shared with (ADR-0153). */
    title: "Groups",
    intro:
      "A group is a list of people. Share a published book with a group from its Publish page, and only they can read it.",
    newGroup: "New group",
    newGroupName: "Group name",
    create: "Create",
    rename: "Rename",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete group",
    confirmDelete: (name: string) =>
      `Delete ${name}? Books shared with it stop being shared with these people.`,
    empty: "No groups yet.",
    people: (c: number) => `${n(c)} ${s(c, "person", "people")}`,
    nobody: "Nobody in this group yet.",
    addPerson: "Add",
    addPersonEmail: "Email they signed in with",
    remove: "Remove",
    failed: "That did not work. Try again.",
    forbidden: "Only the library's creators can manage its groups.",
  },
  translation: {
    blank: "Blank",
    translate: "Translate a book",
    blankNote: "A new, empty book. Add its chapters next.",
    translateNote:
      "A copy of one of your books in another language: its text, cast and descriptions are translated, and its cover, portraits and pictures are reused. Nothing is narrated; you choose a voice for the new language afterwards.",
    from: "Translate",
    into: "Into",
    noSources: "None of your books has both a language and some text yet.",
    estimate: (chapters: number, cost: string) =>
      `${n(chapters)} ${s(chapters, "chapter", "chapters")} · about ${cost}`,
    estimating: "Working out the cost…",
    start: (cost: string) => `Translate · about ${cost}`,
    startFailed: "Could not start the translation.",
    running: (done: number, total: number) => `Translating · ${n(done)} of ${n(total)} chapters`,
    paused: "Translation paused",
    done: (source: string) => `Translated from ${source}`,
    cancelled: "Translation cancelled",
    spent: (cost: string) => `${cost} so far`,
    spentTotal: (cost: string) => `${cost} in all`,
    background:
      "It carries on in the background; you can close the app. Chapters appear as they finish.",
    held: "Chapters can be added once the translation finishes or is cancelled.",
    reasons: {
      refused: "The model declined to translate this text.",
      failed: "The translation service kept failing.",
      budget: "The library's budget for this month is spent. Raise it, then retry.",
      too_long: "The translated chapter is longer than a chapter may be.",
      invalid: "The translation could not be saved as a chapter.",
      source_gone: "The book it is translated from has been deleted.",
    } as Record<string, string>,
    reasonAt: (chapter: number, reason: string) =>
      chapter > 0 ? `Chapter ${n(chapter)}: ${reason}` : reason,
    retry: "Retry",
    retryFailed: "Could not retry.",
    cancel: "Stop translating",
    cancelQuestion: "Stop for good? The chapters translated so far stay.",
    cancelFailed: "Could not stop the translation.",
    estimatedPictures: (c: number) =>
      `${n(c)} ${s(c, "picture was", "pictures were")} placed by estimate — check where ${s(c, "it sits", "they sit")}:`,
    summaryRunning: (done: number, total: number) => `translating ${n(done)} of ${n(total)}`,
    summaryPaused: "translation paused",
    // A linked book (requirement 0038, ADR-0093).
    kind: "Then",
    copyKind: "A copy I can edit",
    copyNote: "Yours from the moment it is made. The source can change; this will not follow it.",
    linkedKind: "Linked to the source",
    linkedNote:
      "It stays a translation of this book: its words, cast and pictures come from the source, and you refresh it when the source changes. You cannot edit them by hand — but you narrate and publish it as any other book.",
    linkedTo: (source: string) => `Linked to ${source}`,
    linkedBlurb: "Its text, cast and pictures come from the source. Refresh it to take up changes.",
    checking: "Looking for changes…",
    upToDate: "Up to date with the source.",
    refresh: "Refresh",
    refreshWithCost: (cost: string) => `Refresh · about ${cost}`,
    refreshFailed: "Could not refresh.",
    refreshing: "Refreshing…",
    changeDetails: "the book's details or its cast",
    changeChapters: (c: number) => `${n(c)} ${s(c, "chapter", "chapters")} changed`,
    changeAdded: (c: number) => `${n(c)} added`,
    changeRemoved: (c: number) => `${n(c)} removed`,
    changePictures: (c: number) =>
      `pictures in ${n(c)} ${s(c, "chapter", "chapters")} — free, and the narration is kept`,
    unlink: "Unlink",
    unlinkQuestion: "Unlink for good? It becomes yours to edit, and cannot be refreshed again.",
    unlinkFailed: "Could not unlink.",
    lockedNote: "Linked to another book, so this comes from the source. Unlink it to edit by hand.",
    // The author's instruction (requirement 0039, ADR-0094).
    howHeading: "How it is translated",
    howNote:
      "This is what the model is told about how the book should read. Change it to ask for a different register — plainer, older, more formal.",
    howReset: "Put the default back",
    rulesNote:
      "These go with it every time and cannot be changed — they are what makes the answer usable. An answer that breaks them is paid for and thrown away.",
    // Trying it on one paragraph (requirement 0040, ADR-0095), on a paragraph
    // the author can change (requirement 0044, ADR-0099).
    tryIt: "Try it on a paragraph",
    tryAnother: "Another paragraph",
    trying: "Translating…",
    tryNote: "One paragraph, a fraction of a cent each press. Nothing is saved.",
    tryFailed: "Could not translate the paragraph.",
    tryLimits:
      "Try it keeps this paragraph, so editing the instruction is the only thing that changes; Another paragraph takes a different one from somewhere in the book. Names are settled by the first call of the real run, so a sample cannot show how they will read.",
    // Changing it on a linked book (requirement 0041, ADR-0096).
    howLinkedNote:
      "What the model is told about how this book should read. Change it, try it on a paragraph, then refresh the book to apply it.",
    howSave: "Save",
    howSaveNote: "Saving changes nothing yet — refreshing is a separate press, below.",
    howSaveFailed: "Could not save the instruction.",
    causeInstruction: "The instruction changed, so the whole book is translated again.",
    audioOutdated: (c: number) =>
      `${n(c)} narrated ${s(c, "chapter", "chapters")} will need re-recording — only the parts whose words change cost anything.`,
    // The conflict inbox (ADR-0098 §8).
    conflictsHeading: "Where the translation disagreed",
    conflictsNote:
      "You fixed these by hand, and the source has since changed under them. Your words are what the book says — this is only what the translation would have said instead.",
    conflictsNone: "Nothing to review.",
    conflictsBadge: (c: number) => `${n(c)} to review`,
    conflictSourceChanged: "What changed in the original",
    conflictYours: "Your wording",
    conflictProposed: "What the translation would say",
    conflictKeep: "Keep mine",
    conflictTake: "Use this",
    conflictKeepAll: "Keep all mine",
    conflictTakeAll: "Use all of these",
    conflictSuperseded:
      "You have since rewritten these words, so this one no longer applies. Keep it to clear it.",
    conflictTitleUnit: "Chapter title",
    conflictChapterUnit: "The whole chapter",
    conflictAudio: (c: number) =>
      `Using ${s(c, "this", "these")} re-records nothing on its own, but the chapter's audio becomes outdated — only the parts whose words change cost anything.`,
    conflictFailed: "Could not settle that.",
    chaptersEdited: (c: number) =>
      `${n(c)} ${s(c, "chapter", "chapters")} you have edited will be re-translated. Your wording is kept; you can review what the translation would have said.`,
    howRefreshNote: "Nothing happens until you refresh the book.",
    // How hard the model is asked to think (requirement 0042, ADR-0097).
    effortHeading: "How hard to think",
    effortNote:
      "More thinking usually reads better and always costs more. The price beside the button moves with it.",
    // The sentence that stops somebody turning the knob up, pressing Refresh
    // and getting a run that makes no calls at all (ADR-0097 §5).
    effortLinkedNote:
      "This applies to whatever the next refresh translates. On its own it re-translates nothing, so no audio is lost by changing it.",
    effortFast: "Fast",
    effortMedium: "Medium",
    effortHigh: "High",
    // Refresh, now beside the instruction that configures it (ADR-0096 Since).
    refreshHeading: "Refresh from the source",
  },
  characters: {
    heading: "Characters",
    note: "The language model reads every chapter and lists the people who recur or matter. Each gets an introduction, which readers see once they meet them, and a description of their whole story, which readers see only if they ask for spoilers. Then the list is yours to edit.",
    noText: "Add a chapter with text first.",
    extract: "Extract characters",
    extractAgain: "Extract again",
    narratorHeading: "Told by",
    narratorNote:
      "For a book told by one of its characters — in the first person, or in letters. Readers see the narrator's face wherever no quoted line is spoken, after the next publish.",
    narratorNone: "Nobody",
    speakersHeading: "Who speaks",
    manySpeak: "Everyone quoted",
    oneSpeaker: "Only the narrator",
    oneSpeakerNote:
      "For letters, an essay or a diary: every quoted line is the narrator's, so who says each line is never asked and costs nothing.",
    oneSpeakerNeedsNarrator: "Choose who tells it first.",
    oneSpeakerFailed: "Could not change who speaks.",
    extracting: "Extracting…",
    estimate: (chapters: number, cost: string) =>
      `Reading ${n(chapters)} ${s(chapters, "chapter", "chapters")} costs about ${cost}.`,
    stale: (chapters: number, cost: string) =>
      `${n(chapters)} ${s(chapters, "chapter has", "chapters have")} changed since — read again for about ${cost}.`,
    progress: (done: number, total: number) => `Reading chapter ${n(done)} of ${n(total)}…`,
    merging: "Compiling the cast…",
    result: (count: number, chapters: number, cost: string) =>
      `${n(count)} ${s(count, "character", "characters")} from ${n(chapters)} ${s(chapters, "chapter", "chapters")} · ${cost}`,
    partial: (failed: number) =>
      `${n(failed)} ${s(failed, "chapter", "chapters")} could not be read. The list is built from the rest.`,
    failed: "The characters could not be extracted.",
    empty: "No characters yet.",
    inChapters: (titles: string) => `In: ${titles}`,
    alsoKnownAs: (names: string) => `Also ${names}`,
    edited: "edited",
    byAuthor: "added by you",
    edit: "Edit",
    add: "Add a character",
    name: "Name",
    aliases: "Other names, separated by commas",
    role: "Role",
    description: "Description: their whole story",
    introduction: "Introduction: who they are when first met, nothing later",
    readersSee: "Readers see",
    forms: {
      heading: "Other forms",
      note: "A disguise or another age — Minerva as Mentes, a hero forty years younger. A form has its own face, so a picture of it is not drawn from theirs.",
      add: "+ Add a form",
      name: "Name of the form",
      aliases: "Names the text uses for it, separated by commas",
      appearance: "How this form looks",
      save: "Save",
      cancel: "Cancel",
      remove: "Remove",
      saveFailed: "Could not save the form.",
      of: (form: string, character: string) => `${form} — ${character} in another form`,
    },
    save: "Save",
    saving: "Saving…",
    saveFailed: "Could not save the character.",
    fill: "Fill with AI",
    filling: "Reading the book…",
    fillHint:
      "Finds this name in the book and fills the empty fields. Nothing is saved until you save.",
    filled: (passages: number) =>
      `Filled from ${n(passages)} ${passages === 1 ? "passage" : "passages"}, for`,
    fillFailed: "Could not fill in this character.",
    delete: "Delete character",
    deleteQuestion: "Delete this character? Extracting again will not bring it back.",
    mergeInto: "Merge into another character",
    mergeChoose: "Choose a character…",
    merge: "Merge",
    extractFailed: "Could not start extracting.",
    loadFailed: "The characters could not be loaded.",
  },
  dialogue: {
    heading: "Who says each line",
    note: "Each quoted line is given to the character who says it, when you ask — check the cast first, since the lines go to the people in it. Lines by people who are not in the cast are marked someone else. You can correct any line.",
    unavailable: "Dialogue attribution is not switched on here.",
    modelHeading: "Model",
    modelDefault: "default",
    modelNote: "Changing it starts nothing; the next run uses it.",
    modelFailed: "Could not change the model.",
    modelUnavailable: "This book's model cannot be used here.",
    progress: (done: number, total: number) =>
      `Finding who says each line: chapter ${n(done)} of ${n(total)}…`,
    result: (lines: number, chapters: number, cost: string) =>
      `${n(lines)} ${s(lines, "line", "lines")} in ${n(chapters)} ${s(chapters, "chapter", "chapters")} · ${cost}`,
    changed: (chapters: number, cost: string) =>
      `${n(chapters)} ${s(chapters, "chapter has", "chapters have")} changed since — update for at most ${cost}.`,
    first: (cost: string) =>
      `Not asked yet. Check the cast first: each line goes to someone in it — at most ${cost}.`,
    find: (chapters: number) =>
      `Find who says what · ${n(chapters)} ${s(chapters, "chapter", "chapters")}`,
    update: "Update",
    updating: "Updating…",
    updateFailed: "Could not start the update.",
    again: (cost: string) =>
      `Ask every chapter again with the cast as it is now, keeping your corrections — at most ${cost}.`,
    askAgain: "Ask again",
    askingAgain: "Asking…",
    againFailed: "Could not start asking again.",
    speakersFound: (kept: number) =>
      kept === 0
        ? "No missing speaker had two or more lines."
        : `${n(kept)} ${s(kept, "speaker", "speakers")} with two or more lines joined the cast.`,
    refused: "Not started: this month's budget would not cover it.",
    failed: "Who says each line could not be worked out.",
    partial: (failed: number) =>
      `${n(failed)} ${s(failed, "chapter", "chapters")} could not be done.`,
    lines: (count: number) => `${n(count)} ${s(count, "line", "lines")}`,
    findLines: "Find their lines",
    findingLines: "Asking…",
    someoneElse: "Someone else",
    notSpeech: "Not speech",
    unknown: "Unknown",
    byYou: "set by you",
    choose: "Who says this?",
    saidBy: (name: string) => `Said by ${name}`,
    correctFailed: "Could not change the speaker.",
    loadFailed: "The lines could not be loaded.",
    oneSpeaker: (name: string) =>
      `One speaker: ${name} says every line. Nothing is asked and nothing is spent.`,
  },
  usage: {
    title: "Usage",
    resets: (period: string) => `${period} · resets on the 1st`,
    thisMonth: "This month",
    loadFailed: "Usage could not be loaded.",
    of: "of",
    budgetUsed: "Budget used this month",
    left: (amount: string) => `${amount} left this month for narration and questions.`,
    wentOn: "What it went on",
    nothingSpent: "Nothing spent yet this month.",
    recent: "Recent charges",
    others: (mine: string) =>
      `This account's own charges come to ${mine}. The rest was spent by other members of the same library.`,
    kinds: {
      search_index: "Book search index",
      narration: "Narration",
      preview: "Pronunciation preview",
      word_timing: "Word timing",
      rewrite: "AI rewrite",
      question: "Question",
      characters: "Characters",
      dialogue: "Who says each line",
      pronunciation: "Pronunciation for ElevenLabs",
      illustration: "Illustrations",
      translation: "Translation",
      voice_hearing: "Hearing voices for casting",
    } as Record<string, string>,
  },
  admin: {
    title: "Admin",
    back: "Admin",
    accounts: (c: number) =>
      `${n(c)} ${s(c, "account", "accounts")} · roles, this month's spend and its limit`,
    accountsFallback: "Accounts",
    loadFailed: "Accounts could not be loaded.",
    badge: "Admin",
    spent: (usd: string) => `${usd} this month`,
    limit: (usd: string) => `${usd} limit`,
    limitTitle: "Monthly limit",
    limitWhat:
      "What this account's library may spend each month. A change applies to the next charge, and refunds nothing.",
    limitNone: "This account has no library, so there is nothing to limit.",
    limitFor: (name: string) => `Monthly limit for ${name}`,
    limitFailed: "Could not change the limit.",
    limitStop: "Stop",
    limitOther: "Other",
    limitOtherLabel: "Another amount, in dollars",
    limitSave: "Save",
    limitInvalid: "That is not an amount.",
    roleFailed: "Could not change the role.",
    localeFailed: "Could not change the language.",
    roleFor: (name: string) => `Role for ${name}`,
    languageFor: (name: string) => `Language for ${name}`,
    noLibrary: "No library",
    roles: { reader: "Reader", creator: "Creator", admin: "Admin" } as Record<string, string>,
    wayIn: { google: "Google", apple: "Apple", tester: "Tester" } as Record<string, string>,
    off: "Switched off",
    switchOff: "Switch off",
    switchOn: "Switch on",
    switchFailed: "Could not switch the account.",
    testerTitle: "New tester login",
    testerWhat:
      "A username and a generated password, for a tester or a reviewer. No email is collected. The password is shown once.",
    testerUsername: "Username",
    testerCreate: "Create",
    testerFailed: "Could not create the tester.",
    recordsTitle: "Reading",
    recordsNone: "No reading time recorded yet.",
    minutes: (m: number) => `${n(m)} min`,
    questionKinds: {
      typed: "Typed",
      meaning: "Meaning",
      explain: "Explain",
      simpler: "Simpler",
    } as Record<string, string>,
    questionsTitle: "Questions, all time",
    sessionsTitle: "Sessions, all time",
    sessionsNone: "No reading sessions recorded yet.",
    sessionsCount: "Sessions",
    sessionsMedian: "Median",
    sessionsLongest: "Longest",
    sessionsEndedBy: {
      switched_away: "Switched away",
      left: "Left the book",
      idle: "Went idle",
    } as Record<string, string>,
    sessionsBook: (count: number, median: string) =>
      `${n(count)} ${count === 1 ? "session" : "sessions"} · median ${median}`,
    duration: (seconds: number) =>
      seconds < 60
        ? `${n(seconds)} s`
        : seconds < 3600
          ? `${n(Math.round(seconds / 60))} min`
          : `${n(Math.floor(Math.round(seconds / 60) / 60))} h ${n(Math.round(seconds / 60) % 60)} min`,
    testerMade: (username: string) =>
      `${username} can sign in in the app with this password. Copy it now: it is not shown again.`,
  },
  legal: {
    privacy: "Privacy policy",
    terms: "Terms of use",
    about: "About",
  },
  traffic: {
    title: "Visitors",
    link: "Visitors",
    what: "Last 30 days, by where people came from. Counted without cookies; one device counts once a day.",
    loadFailed: "The numbers could not be loaded.",
    empty:
      "No visits counted yet. Links in posts should carry ?src=, for example ?src=reddit-hamlet.",
    source: "Source",
    visitors: "Visits",
    returned: "Came back",
    signups: "Signed up",
    total: "Total",
    cohortsTitle: "Sign-up weeks",
    cohortsWhat: "Each week's sign-ups by source, and the median reading session of those readers.",
    cohortsEmpty: "No sign-ups in this range.",
    week: "Week",
    medianSession: "Median session",
  },
  errors: {
    title: "Errors",
    link: "Errors",
    what: "Server failures from the last 14 days, newest first. A reference id a reader quotes, or a push, is found here.",
    loadFailed: "The errors could not be loaded.",
    empty: "No server failures in the last 14 days.",
    user: "Account",
  },
  collections: {
    row: "Collections",
    books: (count: number) => `${n(count)} ${s(count, "book", "books")}`,
    loadFailed: "This collection could not be loaded.",
    notFound: "There is nothing in this collection for you to read.",
    back: "All books",
    // The admin panel's page (requirement 0119).
    link: "Collections",
    title: "Collections",
    what: "Curated lists shown above Browse. Each reader sees only the books they may read; a collection with none for them is hidden.",
    listFailed: "The collections could not be loaded.",
    empty: "No collections yet.",
    name: "Name",
    description: "Description",
    create: "Make a collection",
    save: "Save",
    delete: "Delete",
    deleteQuestion: (name: string) => `Delete “${name}”? Its books stay where they are.`,
    noBooks: "No books in this collection yet.",
    addBook: "Add a book",
    findBook: "Find a book by title, or paste its link",
    addThis: "Add this book",
    moveUp: "Move up",
    moveDown: "Move down",
    remove: "Remove",
    draft: "Not published",
    notPublic: (visibility: string): string => (visibility === "shared" ? "Shared" : "Private"),
    kind: "Kind",
    kindCollection: "Collection",
    kindSeries: "Series",
  },
  // A volume of a series (requirement 0119 T2).
  series: {
    volumeOf: (number: number, of: number) => `Vol. ${n(number)} of ${n(of)}`,
    volumes: (count: number) => `${n(count)} ${s(count, "volume", "volumes")}`,
    previous: "Previous volume",
    next: "Next volume",
  },
  activity: {
    title: "Activity",
    link: "Activity",
    what: "What is running now, and what is billing. Updates itself.",
    updated: (time: string) => `Updated ${time}`,
    loadFailed: "Activity could not be loaded.",
    attention: "Needs attention",
    allClear: "Nothing needs attention.",
    machines: "Machines",
    noMachines: "No machine is running.",
    stoppedToday: "Stopped today",
    work: "Work",
    noWork: "Nothing is running or waiting.",
    other: "Other",
    status: {
      starting: "Starting",
      running: "Running",
      stopped: "Stopped",
    } as Record<string, string>,
    lane: {
      inline: "Server",
      gpu: "GPU",
      kokoro: "Kokoro",
      portrait: "Portrait",
    } as Record<string, string>,
    kind: {
      "render.plan": "Planning narration",
      "render.chunk": "Narrating",
      "align.chunk": "Aligning",
      "render.finalize": "Finishing narration",
      "batch.submit": "Sending to economy",
      "batch.collect": "Collecting economy",
      "export.chapter": "Building a download",
      "narrate.book": "Narrating the book",
      "prepare.piece": "Preparing text",
      "performance_script.chapter": "Preparing for voices",
      "reading.index": "Indexing for search",
      "translation.step": "Translating",
      "characters.extract": "Finding characters",
      "characters.merge": "Building the cast",
      "characters.attribute": "Who says each line",
      "illustration.plan": "Planning pictures",
      "illustration.spot": "Drawing pictures",
      "illustration.portrait": "Drawing portraits",
      "illustration.live_portrait": "Making live portraits",
      "noop.echo": "Queue check",
    } as Record<string, string>,
    duration: (seconds: number) => {
      if (seconds < 60) return `${n(seconds)} s`;
      const minutes = Math.floor(seconds / 60);
      return minutes >= 60
        ? `${n(Math.floor(minutes / 60))} h ${n(minutes % 60)} min`
        : `${n(minutes)} min`;
    },
    up: (d: string) => `up ${d}`,
    ranFor: (d: string) => `ran ${d}`,
    soFar: (cost: string) => `${cost} so far`,
    spent: (cost: string) => `${cost}`,
    perHour: (rate: string) => `${rate}/h`,
    seen: (d: string) => `seen ${d} ago`,
    neverSeen: "not seen yet",
    jobs: (count: number) => `${n(count)} ${s(count, "job", "jobs")}`,
    stopReason: (reason: string) => `Stopped: ${reason}`,
    running: (count: number) => `${n(count)} running`,
    waiting: (count: number) => `${n(count)} waiting`,
    failed: (count: number) => `${n(count)} failed`,
    oldest: (d: string) => `oldest waiting ${d}`,
  },
  device: {
    title: "Sign in on the command line",
    what: "Something on a computer is asking to act as you through sageleaf.io. Approve it only if you just started it yourself.",
    code: "Code",
    asking: "Asking",
    requested: (minutes: number) =>
      minutes < 1
        ? "Asked just now"
        : `Asked ${n(minutes)} ${minutes === 1 ? "minute" : "minutes"} ago`,
    approve: "Approve",
    deny: "Deny",
    approved: "Approved. The command line is signed in; you can close this page.",
    denied: "Denied. Nothing was signed in.",
    notFound: "This code is unknown, has expired, or was already answered.",
    noCode: "Open the link the command line printed.",
    failed: "That did not work.",
    signIn: "Sign in to approve",
  },
  install: {
    prompt: "Read this in the SageLeaf app",
    get: "Get the app",
    dismiss: "Not now",
    appStore: "Download on the App Store",
    googlePlay: "Get it on Google Play",
  },
};

export type Messages = typeof en;

const fa: Messages = {
  meta: {
    num: f,
    dateLocale: "fa-IR-u-ca-gregory",
  },
  common: {
    tryAgain: "دوباره امتحان کنید",
    close: "بستن",
    somethingWrong: "مشکلی پیش آمد.",
    searchLanguage: "جست‌وجوی زبان",
    noLanguage: "زبانی پیدا نشد.",
    somethingWrongTryAgain: "مشکلی پیش آمد. دوباره امتحان کنید.",
    failureReference: "کد پیگیری",
    delete: "حذف",
    keep: "نگه داشتن",
    characters: (c) => `${f(c)} نویسه`,
    language: "زبان",
    loading: "در حال بارگذاری",
    loadingChapter: "در حال بارگذاری فصل",
  },
  nav: {
    brand: "مریم گلی",
    home: "صفحهٔ اصلی مریم گلی",
    library: "کتابخانه",
    browse: "همهٔ کتاب‌ها",
    studio: "استودیو",
    usage: "مصرف",
    admin: "مدیریت",
    signIn: "ورود",
    signOut: "خروج",
    signOutFailed: "هنوز وارد حساب هستید. دوباره امتحان کنید.",
    settings: "تنظیمات",
    myQuotes: "نقل‌قول‌های من",
    accountMenu: (name) => `منوی حساب ${name}`,
    toDark: "حالت تیره",
    toLight: "حالت روشن",
  },
  quotes: {
    title: "نقل‌قول‌های من",
    empty:
      "هنوز چیزی نگه نداشته‌اید. در برنامه روی سطری انگشت نگه دارید، یا وقتی سطری پررنگ است «پرسیدن» را بزنید، سپس «نگه‌داشتن».",
    failed: "نقل‌قول‌ها باز نشدند.",
    retry: "دوباره امتحان کنید",
    share: "اشتراک‌گذاری به‌صورت تصویر",
    making: "در حال ساختن تصویر…",
    download: "دریافت",
    shareTo: "اشتراک‌گذاری",
    close: "بستن",
    tooLong:
      "این نقل‌قول برای یک تصویر بلند است. بخش کوتاه‌تری از آن را در فصل نگه دارید و همان را به اشتراک بگذارید.",
    failedPicture: "تصویر ساخته نشد. دوباره امتحان کنید.",
    place: (book, chapter) =>
      chapter ? `\u2068${book}\u2069 · \u2068${chapter}\u2069` : `\u2068${book}\u2069`,
  },
  drawer: {
    menu: "منو",
    closeMenu: "بستن منو",
    contents: "فهرست فصل‌ها",
    editChapter: "ویرایش این فصل در استودیو",
    chapters: "فصل‌ها",
  },
  settings: {
    title: "تنظیمات",
    back: "بازگشت",
    profile: "حساب من",
    profileNote: "حساب شما و زبانی که برنامه با شما حرف می‌زند. با حسابتان ذخیره می‌شود.",
    reading: "خواندن",
    readingNote: "ظاهر فصل‌ها در صفحهٔ خواندن.",
    account: "حساب",
    deleteAccount: "حذف حساب",
    deleteNote:
      "حساب شما، پیشرفت خواندن و کتابخانه‌تان و کتاب‌هایی که ساخته‌اید و کسی نمی‌خواند حذف می‌شوند. کتابی که منتشر کرده‌اید برای خوانندگانش می‌ماند.",
    deleteQuestion: "حسابتان برای همیشه حذف شود؟ این کار برگشت‌پذیر نیست.",
    deleteFailed: "حساب حذف نشد. چیزی پاک نشد.",
    textSize: "اندازهٔ متن",
    smaller: "متن کوچک‌تر",
    larger: "متن بزرگ‌تر",
    sizeOf: (i, total) => `اندازهٔ متن ${f(i)} از ${f(total)}`,
    typeface: "قلم",
    typefaceNote: "فارسی همیشه با وزیرمتن نوشته می‌شود، هر قلمی که انتخاب کنید.",
    theme: "رنگ صفحه",
    themes: { auto: "خودکار", light: "روشن", sepia: "کاغذی", dark: "تیره", night: "شب" },
    autoplay: "پخش خودکار فصل بعد",
    autoplayNote: "وقتی فصلی که به آن گوش می‌دهید تمام شود، فصل بعد پس از ۴۵ ثانیه پخش می‌شود.",
    sample: "نمونه",
    sampleText: "در زندگی زخم‌هایی هست که مثل خوره روح را آهسته در انزوا می‌خورد و می‌تراشد.",
  },
  reader: {
    download: "دریافت کل فصل",
    chapterNav: "فصل‌های دیگر",
    previousChapter: "فصل قبل",
    nextChapter: "فصل بعد",
    nextChapterIn: (seconds) => `فصل بعد تا ${f(seconds)} ثانیهٔ دیگر`,
    readSoFar: "خوانده‌شده تا این‌جا",
    blockNote: "یادداشت",
    blockOriginal: "متن اصلی",
    preparing: "در حال آماده کردن فایل…",
    saveMp3: "ذخیرهٔ کل فصل (MP3)",
    downloadSlow:
      "هنوز در حال ساخته شدن است — فصل بلند چند دقیقه طول می‌کشد. بدون باز بودن این صفحه هم ادامه دارد؛ بعداً برگردید و دوباره «دریافت» را بزنید.",
    downloadFailed: "فایل آماده نشد.",
    characters: "شخصیت‌ها",
    castEmpty: "شخصیت‌ها همین‌جا پیدا می‌شوند، هر وقت با آن‌ها آشنا شوید.",
    castSummary: (met, unmet) =>
      met === 0
        ? "با شنیدن داستان با آن‌ها آشنا می‌شوید."
        : `${f(met)} شخصیت تا این‌جا` + (unmet > 0 ? `، ${f(unmet)} شخصیت در ادامه` : ""),
    arcsTitle: "مسیر شخصیت‌ها",
    spoilers: "لو رفتن داستان",
    arcsWarning: "سرانجام همهٔ شخصیت‌ها — از جمله فصل‌هایی که هنوز به آن‌ها نرسیده‌اید.",
    showArcs: "نمایش",
    hideArcs: "پنهان کردن",
    arcsLoading: "در حال بارگذاری…",
    castFailed: "شخصیت‌ها بارگذاری نشدند.",
  },
  signIn: {
    reasons: {
      declined: "ورود لغو شد. هر وقت خواستید دوباره تلاش کنید.",
      expired: "این ورود بیش از حد طول کشید و منقضی شد. لطفاً دوباره تلاش کنید.",
      disabled: "این حساب غیرفعال شده است.",
      conflict: "نتوانستیم شما را وارد کنیم: این نشانی رایانامه پیش‌تر به حساب دیگری ثبت شده است.",
      failed: "نتوانستیم شما را وارد کنیم. لطفاً دوباره تلاش کنید.",
    },
  },
  landing: {
    title: "این کتاب را تنها نمی‌خوانید",
    blurb:
      "بخوانید یا گوش کنید و دربارهٔ هر سطری بپرسید. پاسخ از صفحه‌هایی می‌آید که خوانده‌اید، نه از صفحه‌های پیش رو. چهرهٔ هر شخصیت کنار سخنانش است، و در کتاب‌های روایت‌شده صدایش هم.",
    start: "شروع خواندن",
    startNote: "همین‌جا باز می‌شود، بی‌نیاز به حساب.",
    signIn: "برای شروع وارد شوید",
    heroAlt:
      "هملت با روایت صوتی در مریم گلی: واژه‌های خوانده‌شده برجسته، و چهرهٔ برناردو کنار سخنانش",
    facesTitle: "شخصیت‌ها را همان‌جا بشناسید که وارد می‌شوند",
    facesBody:
      "هر شخصیت وقتی پیدا می‌شود که داستان معرفی‌اش کند. آن‌هایی که هنوز نیامده‌اند پنهان می‌مانند.",
    features: [
      {
        title: "بپرسید، بی‌آنکه داستان لو برود",
        body: "روی هر سطری بزنید، سپس «پرسیدن» را بزنید. پاسخ فقط از صفحه‌های پشت سر می‌آید، نه از پیش رو؛ پایان داستان مال خودتان می‌ماند.",
        alt: "پاسخی به «چرا روح شبیه شاه پیشین است؟» از بخش خوانده‌شده",
      },
      {
        title: "هر کتاب روی میز خودش باز می‌شود",
        body: "کتاب روی میزِ اتاقِ مطالعه است، در روشنای روز یا زیر نور شمع. لمسش کنید تا همان‌جا که مانده بودید باز شود.",
        alt: "هملت روی میز اتاق مطالعه، با فصل‌هایش در پایین",
      },
      {
        title: "تصویرهایی از خود متن",
        body: "صحنه‌ها از روی خود متن کشیده شده‌اند و همان‌جای فصل می‌نشینند که رخ می‌دهند.",
        alt: "فصلی از آلیس در سرزمین عجایب با تصویر آلیس و خرگوش سفید",
      },
      {
        title: "اتاق مطالعهٔ خودتان",
        body: "کتاب‌هایتان در قفسه‌های گردویی‌اند. با یک لمس از همان‌جا که مانده بودید ادامه دهید، در وب یا در برنامه.",
        alt: "کتابخانه: کتابی برای ادامه، و قفسه‌ها",
      },
    ],
    clipsTitle: "ببینید چطور کار می‌کند",
    clips: [
      {
        title: "دربارهٔ هر سطری بپرسید",
        body: "روی سطری بزنید و بپرسید. پاسخ از صفحه‌هایی می‌آید که خوانده‌اید، نه از صفحه‌های پیش رو.",
      },
      {
        title: "پرسش خودتان را بپرسید",
        body: "پرسش بعدی را بنویسید. پاسخ از همان صفحه‌ها می‌آید و پایان داستان دست‌نخورده می‌ماند.",
      },
      {
        title: "ساده‌ترش را بخواهید",
        body: "با یک ضربه، سطری کهن به زبان ساده و امروزی درمی‌آید.",
      },
      {
        title: "روی واژه‌ای نگه دارید",
        body: "معنایش همان دم می‌آید، با معنایی که در این سطر دارد.",
      },
      {
        title: "سطری را نگه دارید",
        body: "جمله‌ای را که می‌خواهید به یاد بماند نگه دارید؛ روی صفحهٔ اصلی گوشی‌تان منتظر است.",
      },
      {
        title: "مجموعه‌ها را بگردید",
        body: "کتاب‌هایی که بر پایهٔ موضوع کنار هم آمده‌اند، از رواقیان تا مرگ سقراط. ورق بزنید و یکی را باز کنید.",
      },
    ],
    clipPlay: "پخش",
    clipPause: "توقف",
    more: "کتاب‌های بیشتر",
    shelfTitle: "همهٔ قفسه",
    legend:
      "کتاب‌هایی که «همین حالا بخوانید» دارند همین‌جا باز می‌شوند، بی‌نیاز به حساب. پلاس همهٔ کتاب‌ها را باز می‌کند.",
  },
  free: {
    title: "خواندن رایگان",
    note: "هرکسی می‌تواند این کتاب را بدون حساب بخواند و در گالری عمومی دیده می‌شود. باقی چیزها ورود می‌خواهد، از جمله پرسیدن از دستیار مریم گلی دربارهٔ آن.",
    on: "همه می‌توانند بخوانند.",
    off: "فقط خوانندگان وارد شده.",
    turnOn: "رایگانش کن",
    turnOff: "فقط برای واردشده‌ها",
    working: "در حال ذخیره…",
    publishFirst: "اول کتاب را منتشر کنید — هنوز چیزی برای خواندن بازدیدکننده نیست.",
    publicFirst: "همه نمی‌توانند این کتاب را بخوانند. پیش از رایگان کردن، آن را برای همه باز کنید.",
    failed: "تغییر انجام نشد. دوباره امتحان کنید.",
  },
  chat: {
    title: "از کتاب بپرسید",
    ask: "بپرسید",
    newChat: "گفتگوی تازه",
    aboutPlace: (c) => `دربارهٔ فصل ${f(c)}، تا جایی که رسیده‌اید`,
    empty: "دربارهٔ یک شخصیت، یک واژه یا اتفاقی که پیش‌تر افتاد بپرسید.",
    emptyNotStory: "دربارهٔ یک واژه، یک نام یا معنای یک بند بپرسید.",
    withoutLine: "بدون این سطر بپرسید",
    remaining: (r) => `${f(r)} پرسش دیگر در این ساعت`,
    yourQuestion: "پرسش شما",
    placeholder: "دربارهٔ کتاب بپرسید",
    placeholderFollowUp: "پرسش بعدی",
    send: "فرستادن",
    reading: "در حال خواندن…",
    fromTheBook: "از متن کتاب",
    notInBook: "کتاب پاسخ مستقیمی برای این پرسش ندارد.",
    pageOnly: "از کتاب تا همین‌جا — بدون جستجو، و هیچ‌چیز جلوتر از شما.",
    failed: "پاسخی به دست نیامد. دوباره امتحان کنید.",
    reopen: "باز کردن گفتگو دربارهٔ این سطر",
    whoIs: (name) => `${name} کیست؟`,
    meaningOf: (word) => `معنی ${word}`,
    meaning: "معنی",
    hardWords: "واژه‌ها و اصطلاح‌های دشوار این سطر",
    explain: "توضیح",
    explainThis: "این را توضیح بده",
    simpler: "ساده‌تر",
    sayItSimpler: "ساده‌تر بگو",
    soFar: "تا اینجا دربارهٔ او چه می‌دانم؟",
    soFarNote: "یک پرسش حساب می‌شود. فقط آنچه تا اینجا خوانده‌اید به کار می‌رود.",
    soFarReading: "در حال مرور…",
    soFarFailed: "پاسخی به دست نیامد. دوباره امتحان کنید.",
    mode: "از چه چیزی پاسخ بدهد",
    modeSpoilerFree: "بدون لو دادن",
    modeOpen: "آزاد",
    modeSpoilerFreeNote: "فقط آنچه خوانده‌اید. هیچ‌چیز از جلوتر.",
    modeOpenNote:
      "همهٔ کتاب، حتی آنچه جلوتر از شماست، به‌علاوهٔ آنچه دربارهٔ این اثر می‌داند. ممکن است داستان را لو بدهد.",
    openAnswer: "شامل دانستهٔ آن بیرون از متن کتاب است.",
    spoilerNote: "ممکن است داستان را لو بدهد.",
    spoilerSwitch: (mode: string) => `برای رفتن به «${mode}» بزنید.`,
    nothingRead: "در آغاز کتاب هستید؛ هنوز چیزی خوانده نشده که از آن پاسخ بدهد.",
    askOpen: "به‌جایش آزاد بپرسید",
    signInToChat: "برای گفتگو دربارهٔ این کتاب با دستیار مریم گلی وارد شوید.",
    questionsPaused:
      "پرسش‌ها برای امروز متوقف شده‌اند؛ نیمه‌شب به وقت اقیانوس آرام (کالیفرنیا) برمی‌گردند.",
  },
  report: {
    action: "گزارش یک مشکل",
    portrait: "گزارش مشکلی در این چهره",
    picture: "گزارش مشکلی در این تصویر",
    title: "گزارش یک مشکل",
    note: "چه چیزی درست نیست؟ (اختیاری)",
    send: "ارسال",
    sending: "در حال ارسال…",
    cancel: "انصراف",
    done: "تمام",
    sent: "برای کسانی که از این کتاب نگهداری می‌کنند فرستاده شد. آن را بررسی می‌کنند و در ویرایشی بعدی درستش می‌کنند؛ لازم نیست کار دیگری بکنید.",
    failed: "گزارش فرستاده نشد. اتصال خود را بررسی کنید و دوباره امتحان کنید.",
  },
  picture: {
    close: "بستن تصویر",
    open: "باز کردن این تصویر در تمام صفحه",
  },
  player: {
    play: "پخش",
    pause: "مکث",
    position: "جای پخش در فصل",
    positionOf: (at, total) => `${at} از ${total}`,
    follow: "دنبال کردن",
    playbackRetrying: "پخش قطع شد. در حال از سر گرفتن…",
    playbackFailed: "پخش متوقف شد. برای از سر گرفتن، پخش را بزنید.",
    audioIncomplete: "بخش‌هایی از این فصل هنوز صدا ندارند و خواندن از آن‌ها می‌گذرد.",
    speed: "سرعت پخش",
    speedOf: (rate) => `سرعت پخش، اکنون ${rate}`,
    recordingTitle: "این روایت چطور ساخته شده",
    recordingModel: "مدل گفتار",
    recordingVoice: "صدا",
    recordingMode: "حالت",
    recordingModeFast: "سریع",
    recordingModeEconomy: "اقتصادی",
    recordingParts: "بخش‌ها",
    recordingPartsValue: (parts, chars) => `${f(parts)}، هرکدام حدود ${f(chars)} نویسه`,
    recordingExtras: "همچنین",
    recordingReadCut: "هر بخش جملهٔ پیش از خود را هم خوانده است",
    recordingPrepared: "متن برای راوی آماده شده است",
    recordingMade: "زمان ضبط",
    recordingNote:
      "این همان صدایی است که می‌شنوید. تغییر تنظیمات تا زمانی که فصل دوباره روایت نشود، آن را عوض نمی‌کند.",
  },
  correction: {
    title: "اصلاح تلفظ",
    hintNoteIPA:
      "الون‌لبز راهنمای نوشتاری را دنبال نمی‌کند، پس راهنما به آوانگاری (IPA) تبدیل می‌شود و الون‌لبز آن را به جای واژه می‌خواند. گوش کنید تا مطمئن شوید. برای نوشتن IPA خودتان، آن را میان دو خط مورب بنویسید، مانند /ɾæmeˈhɒ/.",
    sentAsIPA: (ipa) => `الون‌لبز واژه را /${ipa}/ می‌خواند`,
    hintsUnsupported: (model) =>
      `به ${model} فقط متن داده می‌شود، پس راهنما در این کتاب اثری ندارد. به جایش واژه را جور دیگری بنویسید و گوش کنید.`,
    respell: "املای دیگر",
    sayItLike: "این‌طور بخوان…",
    respellNote:
      "هیچ املایی همیشه بهتر نیست؛ املایی که یک جمله را درست می‌کند ممکن است جملهٔ دیگری را خراب کند. پیش از نگه داشتن، گوش کنید.",
    respellNoSuggestions:
      "املاهایی که اینجا پیشنهاد می‌شوند فارسی‌اند — یک حرکت، یا حرفی که به جای هم‌آوای خودش می‌نشیند — پس برای این واژه پیشنهادی نیست. املایی بنویسید که درست به گوش برسد و گوش کنید.",
    typeSpelling: "یا املایی بنویسید",
    hintNote:
      "راهنما در مقدمه‌ای می‌آید که مدل از آن پیروی می‌کند و هرگز بلند نمی‌خواندش. تنها راه تعیین تکیه همین است، چون املا اغلب تکیه را نشان نمی‌دهد. حروف لاتین جایشان اینجاست، نه در املای دیگر.",
    hintPlaceholder: "مثلاً «khar-goo-SHEE»",
    rendering: "در حال ساخت…",
    listen: "شنیدن",
    rendersOne: "فقط همین یک جمله ساخته می‌شود",
    leftThisHour: (r) => ` · ${f(r)} بار دیگر در این ساعت`,
    applyTo: "اعمال بر",
    scopeChapter: "این فصل",
    scopeBook: "این کتاب",
    scopeOrg: "کل کتابخانه",
    existing: (scope) =>
      `این واژه برای ${scope} اصلاح شده است. تغییرش دهید و ذخیره کنید، یا حذفش کنید.`,
    existingVariant: (scope) =>
      `اصلاحی برای املای دیگری از این واژه برای ${scope} ذخیره شده است، پس اینجا اثری ندارد:`,
    saving: "در حال ذخیره…",
    save: "ذخیره",
    update: "به‌روزرسانی",
    remove: "حذف",
    removing: "در حال حذف…",
    savingNote: "ذخیره کردن دوباره روایت نمی‌کند؛ اصلاح‌ها را جمع کنید و یک بار دوباره بسازید.",
  },
  edit: {
    editText: "ویرایش",
    editHint: "ویرایش متن و تصویرها",
    done: "تمام",
    paragraph: (i) => `بند ${f(i)}`,
    saving: "در حال ذخیره…",
    saved: "ذخیره شد",
    unsaved: "هنوز ذخیره نشده",
    savesAsYouGo: "خودکار ذخیره می‌شود",
    saveFailed: "متن ذخیره نشد.",
    retry: "دوباره",
    restored: "تغییرهایی که دفعهٔ پیش ذخیره نشده بودند برگردانده شدند.",
    staleCopy:
      "تغییرهای ذخیره‌نشدهٔ دفعهٔ پیش روی نسخهٔ قدیمی‌تری از این فصل بودند که از آن زمان تغییر کرده است.",
    useStaleCopy: "نسخهٔ ذخیره‌نشدهٔ من",
    discardStaleCopy: "همان متن تازه‌تر بماند",
    passageMoved: "آن بخش تغییر کرده، پس نسخهٔ تازه جایگذاری نشد.",
    rewriteSelection: "بازنویسی با هوش مصنوعی",
    selectToRewrite: "بخشی از متن را انتخاب کنید تا با هوش مصنوعی بازنویسی شود.",
    changeTitle: "تغییر این سطر",
    fixPronunciation: (word) => `اصلاح تلفظ «${word}»`,
    editHere: "ویرایش متن همین‌جا",
    rewriteHere: "بازنویسی با هوش مصنوعی",
    lineNotInText: "این سطر پس از روایت تغییر کرده و در متن پیدا نشد.",
    rewriteTitle: "بازنویسی با هوش مصنوعی",
    selected: "بخش انتخاب‌شده",
    empty:
      "بگویید این بخش چگونه تغییر کند: کوتاه‌تر، گرم‌تر، با راوی اول شخص. هر پاسخ نسخهٔ تازه‌ای است که می‌توانید باز هم تغییرش را بخواهید.",
    yourInstruction: "چگونه تغییر کند",
    placeholder: "چگونه تغییر کند؟",
    placeholderFollowUp: "دیگر چه تغییری بدهد؟",
    send: "فرستادن",
    writing: "در حال نوشتن…",
    version: (i) => `نسخهٔ ${f(i)}`,
    replace: "جایگزین کردن انتخاب",
    insertAfter: "افزودن پس از آن",
    failed: "بازنویسی انجام نشد. دوباره امتحان کنید.",
    notSaved: "تا فصل را ذخیره نکنید، چیزی ذخیره نمی‌شود.",
  },
  images: {
    current: "تصویر فعلی",
    addHere: "افزودن تصویر اینجا",
    addAtTop: "افزودن تصویر در آغاز فصل",
    title: "تصویر برای این بخش",
    titleTop: "تصویر برای آغاز فصل",
    replaceTitle: "جایگزینی این تصویر",
    replace: "جایگزینی",
    describeTab: "توصیفش کن",
    uploadTab: "بارگذاری",
    empty:
      "توصیف از همین بخش و متن پیرامونش نوشته می‌شود، و پیش از کشیدن هر تصویری می‌توانید واژه‌واژهٔ آن را تغییر دهید.",
    suggest: "پیشنهاد توصیف",
    placeholder: "تصویر چه چیزی را نشان دهد؟",
    seedHint: "در کادر پایین آغازش کنید — یا خالی بفرستید تا پیشنهادی نوشته شود.",
    placeholderFollowUp: "چه چیزی تغییر کند؟",
    yourInstruction: "تصویر چگونه تغییر کند",
    send: "فرستادن",
    writing: "در حال نوشتن…",
    describeFailed: "توصیف نوشته نشد. دوباره امتحان کنید.",
    description: "توصیف",
    descriptionHint: "پیش از کشیدن، این واژه‌ها را ویرایش کنید؛ تمام دستور همین است.",
    shape: "شکل",
    shapeNames: {
      "4:3": "افقی",
      "3:4": "عمودی",
      "1:1": "مربع",
      "16:9": "پهن",
    } as Record<string, string>,
    quality: "کیفیت",
    qualityNames: {
      standard: "معمولی",
      pro: "بهترین",
    } as Record<string, string>,
    draw: "این تصویر را بکش",
    drawAgain: "یکی دیگر بکش",
    updateAndDraw: "اصلاح و کشیدن",
    updateWords: "فقط اصلاح متن",
    descriptionUpdated: "توضیح اصلاح شد — بخوانیدش، بعد بکشید.",
    showBrief: "نمایش توضیح",
    hideBrief: "پنهان‌کردن توضیح",
    drawing: "در حال کشیدن…",
    updating: "در حال اصلاح توضیح…",
    drawFailed: "تصویر کشیده نشد. توصیف را تغییر دهید و دوباره امتحان کنید.",
    costEach: (amount) => `هر کدام ${amount}`,
    spentHere: (amount) => `تا اینجا ${amount} برای این تصویر خرج شده است`,
    candidates: "تصویرهای کشیده‌شده",
    use: "همین را بگذار",
    placing: "در حال گذاشتن…",
    placeFailed: "تصویر گذاشته نشد. فصل را دوباره بارگذاری کنید.",
    chooseFile: "انتخاب تصویر",
    orDropImage: "یا یک JPEG، PNG یا WebP را اینجا رها کنید",
    uploading: "در حال بارگذاری…",
    uploadFailed: "بارگذاری نشد. تصویر باید JPEG، PNG یا WebP و کمتر از ۱۲ مگابایت باشد.",
    caption: "زیرنویس",
    captionPlaceholder: "زیرنویس، زیر تصویر نمایش داده می‌شود",
    alt: "توصیف برای صفحه‌خوان",
    altPlaceholder: "تصویر چه چیزی را نشان می‌دهد",
    altMissing: "بدون توصیف برای صفحه‌خوان",
    notPublished: "هنوز منتشر نشده",
    save: "ذخیره",
    saved: "ذخیره شد",
    remove: "حذف",
    removeConfirm: "این تصویر از فصل حذف شود؟",
    removeFailed: "این تصویر حذف نشد و هنوز در فصل است.",
    saveFailed: "شرح ذخیره نشد.",
    moveFailed: "تصویر جابه‌جا نشد.",
    move: "جابه‌جایی",
    moveHere: "همین‌جا بگذار",
    moving: "جایش را انتخاب کنید",
    cancelMove: "انصراف",
    generatedNote: "از روی یک توصیف کشیده شده",
    drawnFrom: (names) => `از روی چهرهٔ: ${names.join("، ")}`,
    castLabel: "در این تصویر:",
    castAdd: "افزودن شخصیت",
    castDone: "تمام",
    castFull: "حداکثر چهار نفر.",
    castRemove: (name) => `برداشتن ${name} از این تصویر`,
    drawnFromNobody: "کسی انتخاب نشده؛ شخصیتی بیفزایید تا چهره‌اش حفظ شود.",
    castNoneYet: "این کتاب هنوز شخصیتی ندارد.",
    besideLabel: "همان فضای:",
    besideNone: "چیزی انتخاب نشده؛ هر تصویر فضای خودش را می‌سازد.",
    besideAdd: "استفاده از تصویری پیشین",
    besideWhy:
      "فضا، نور و اشیا منتقل می‌شوند؛ نه آدم‌ها، نه قاب‌بندی و نه آن لحظه. توصیفی که هر کدام از رویش کشیده شده همراهش می‌رود.",
    besideRemove: (chapter) => `کنار گذاشتن تصویرِ ${chapter}`,
    besideFull: (n) => `حداکثر ${f(n)} تا؛ سومی جای قدیمی‌ترین را می‌گیرد.`,
    illustrateChapter: "تصویرگری کل این فصل",
    illustrateWhy:
      "مدل فصل را می‌خواند، جای تصویرها را انتخاب می‌کند و یکی‌یکی می‌کشدشان — هر کدام در همان فضای قبلی. تصویرها سر جایشان می‌نشینند و تا وقتی منتشر نکنید پیش‌نویس‌اند.",
    howManyPictures: "چند تصویر",
    illustrateStart: "تصویرگری فصل",
    illustrateStarting: "در حال شروع…",
    illustrateFailed: "شروع نشد. دوباره تلاش کنید.",
    runPlanning: "در حال خواندن فصل…",
    runDrawing: (at, of) => `در حال کشیدن ${f(at)} از ${f(of)}…`,
    runDrawn: (drawn, planned) => `${f(drawn)} تصویر از ${f(planned)} کشیده شد.`,
    runShort: (drawn, planned, wanted) =>
      `${f(drawn)} تصویر از ${f(planned)} کشیده شد؛ ${f(wanted)} تا خواسته شده بود.`,
    runNone: "هیچ تصویری کشیده نشد.",
    runStopped: (drawn) =>
      drawn === 0 ? "متوقف شد. تصویری گذاشته نشد." : `متوقف شد. ${f(drawn)} تصویر گذاشته شد.`,
    stopRun: "توقف",
    stoppingRun: "در حال توقف…",
    stopRunFailed: "متوقف نشد. دوباره تلاش کنید.",
    runReason: (reason) =>
      ({
        needs_face: "یکی از آدم‌های این تصویر هنوز چهره ندارد.",
        text_changed: "بندی که برایش بود ویرایش شده است.",
        nothing: "مدل جایی ارزشِ تصویر ندید.",
        refused: "مدل از کشیدنش سر باز زد.",
        budget: "بودجهٔ این کتابخانه تمام شده است.",
        failed: "مدل در دسترس نبود.",
        timed_out: "پاسخی نیامد.",
      })[reason] ?? "مشکلی پیش آمد.",
    uploadedNote: "بارگذاری‌شده",
    unplaced: (n) => `${f(n)} تصویر جایش را از دست داده است`,
    unplacedWhy:
      "بندی که هر کدام دنبالش می‌آمد ویرایش شده است. آن را به بندی که هنوز هست ببرید یا حذفش کنید.",
    followed: (text) => `دنبال این می‌آمد: ${text}`,
    countInChapter: (n) => `${f(n)} تصویر`,
    none: "هنوز تصویری در این فصل نیست.",
    loadFailed: "تصویرها بارگذاری نشد.",
    portraitEmpty:
      "توصیف از فهرست شخصیت‌ها و بخش‌هایی که در آن‌ها می‌آید نوشته می‌شود، و پیش از کشیدن هر تصویری می‌توانید واژه‌واژهٔ آن را تغییر دهید.",
    portraitSuggest: "پیشنهاد چهره",
    portraitPlaceholder: "این شخصیت چه شکلی است؟",
    coverEmpty:
      "توصیف از معرفی کتاب، فصل‌ها و شخصیت‌های آن نوشته می‌شود، و پیش از کشیدن هر تصویری می‌توانید واژه‌واژهٔ آن را تغییر دهید. اگر شخصیتی به آن بیفزایید، چهره‌اش از تصویر خودش می‌آید.",
    coverSuggest: "پیشنهاد جلد",
    coverPlaceholder: "جلد چه چیزی را نشان دهد؟",
    needsFaces: (names) =>
      `${names.join("، ")} هنوز تصویری ندارد، پس این تصویر ساخته نمی‌شود. اول در صفحهٔ شخصیت‌ها برایشان چهره بسازید، یا آن‌ها را از این تصویر بردارید.`,
  },
  portraits: {
    title: (name) => `چهرهٔ ${name}`,
    add: (name) => `افزودن چهره برای ${name}`,
    change: (name) => `تغییر چهرهٔ ${name}`,
    none: "بدون چهره",
    remove: "حذف",
    removeFailed: "این چهره حذف نشد.",
    castTitle: "اول شخصیت‌های این کتاب را پیدا کنید",
    castWhy:
      "هر تصویر یک صحنه از روی چهرهٔ شخصیت‌هایی که نامشان در توصیف آمده کشیده می‌شود. همین است که آن‌ها را از فصلی به فصل دیگر یک آدم نگه می‌دارد، پس اول کتاب برای یافتن شخصیت‌هایش خوانده می‌شود.",
    castLazy: "تصویری که نامشان را بیاورد، اول چهرهٔ آن‌ها را می‌خواهد.",
    castNotExtracted: "هنوز کسی این کتاب را برای یافتن شخصیت‌هایش نخوانده است.",
    castMissing: (names) =>
      names.length === 1
        ? `${names[0]} هنوز چهره ندارد.`
        : `${names.slice(0, -1).join("، ")} و ${names[names.length - 1]} هنوز چهره ندارند.`,
    castMissingCount: (missing, total) =>
      `${f(missing)} شخصیت از ${f(total)} شخصیت این کتاب هنوز چهره ندارند.`,
    castProgress: (done, total) => `${f(done)} شخصیت از ${f(total)} شخصیت چهره دارد.`,
    castStale: (n_) =>
      `${f(n_)} فصل پس از خواندن شخصیت‌ها تغییر کرده است. ممکن است شخصیت‌های تازه جا مانده باشند.`,
    goToCast: "رفتن به شخصیت‌ها",
    batchStart: "چند چهره با هم",
    batchPick:
      "کسانی را که می‌خواهید تیک بزنید. برای هر کدام توصیفی از روی کتاب نوشته می‌شود و تصویری مربع از روی آن کشیده می‌شود که جای چهرهٔ فعلی‌شان را می‌گیرد. بعداً می‌توانید هر کدام را دوباره بکشید.",
    batchAll: "همه",
    batchNone: "هیچ‌کدام",
    batchWithoutFaces: "بی‌چهره‌ها",
    batchCancel: "انصراف",
    batchDraw: (count) => `کشیدن ${f(count)} چهره`,
    batchChoose: (name) => `کشیدن چهرهٔ ${name}`,
    batchDrawing: "در حال کشیدن…",
    batchQueued: (count) => `${f(count)} چهره در حال کشیده شدن است. می‌توانید از این صفحه بروید.`,
    batchQueueFailed: "کشیدن این چهره‌ها شروع نشد.",
    batchReasons: {
      refused: "مدل از کشیدن این چهره سر باز زد. توصیفش را عوض کنید و دوباره امتحان کنید.",
      budget: "بودجهٔ کتابخانه تمام شده است.",
      failed: "سرویس تصویر پیاپی خطا داد. دوباره امتحان کنید.",
      timed_out: "بیش از حد طول کشید و رها شد. دوباره امتحان کنید.",
    } as Record<string, string>,
    batchFailedFallback: "این چهره کشیده نشد.",
  },
  live: {
    start: "ساختن چهره‌های زنده",
    pick: "کسانی را که می‌خواهید چهره‌شان جان بگیرد تیک بزنید: سر می‌چرخد، سینه نفس می‌کشد و چشم‌ها پلک می‌زنند. فقط شخصیت‌هایی که چهره دارند را می‌شود برگزید. از روی چهره ساخته می‌شود، پس چهرهٔ تازه آن را پاک می‌کند.",
    all: "همهٔ چهره‌دارها",
    without: "آن‌هایی که ندارند",
    none: "هیچ‌کدام",
    cancel: "انصراف",
    make: (count) => `ساختن ${f(count)} چهرهٔ زنده`,
    choose: (name) => `ساختن چهرهٔ زندهٔ ${name}`,
    making: "در حال جان گرفتن…",
    queued: (count) =>
      `${f(count)} چهرهٔ زنده در حال ساخته شدن است. اولی ممکن است تا راه افتادن یک ماشین چند دقیقه طول بکشد. می‌توانید از این صفحه بروید.`,
    queueFailed: "ساختن این چهره‌های زنده شروع نشد.",
    badge: "زنده",
    badgeLabel: (name) => `${name} چهرهٔ زنده دارد`,
    heading: "چهرهٔ زنده",
    note: "نشانگر را رویش بچرخانید. خواننده‌ها پس از انتشار آن را می‌بینند.",
    reasons: {
      refused: "این چهره جان نگرفت.",
      budget: "بودجهٔ کتابخانه تمام شده است.",
      failed: "ماشین پیاپی خطا داد. دوباره امتحان کنید.",
      timed_out: "بیش از حد طول کشید و رها شد. دوباره امتحان کنید.",
      expired: "بیش از حد منتظر یک ماشین ماند. دوباره امتحان کنید.",
      portrait_changed: "چهره در حین ساختن تغییر کرد. دوباره امتحان کنید.",
    } as Record<string, string>,
    failedFallback: "این چهرهٔ زنده ساخته نشد.",
  },
  cover: {
    title: "جلد کتاب",
    note: "یک تصویر برای کل کتاب. همان چیزی است که خواننده در کتابخانه می‌بیند. جلد تازه تا وقتی منتشر نشود پیش‌نویس است، مثل هر چیز دیگری که به خواننده می‌رسد. بارگذاری رایگان است؛ ساختن آن تقریباً به اندازهٔ هر تصویر دیگری هزینه دارد.",
    none: "هنوز جلدی ندارد",
    add: "افزودن جلد",
    change: "تغییر جلد",
    remove: "حذف",
    removeQuestion: "این جلد حذف شود؟",
    removeFailed: "جلد حذف نشد.",
    missingFaces: (names) =>
      `${names.join("، ")} هنوز تصویری ندارد، پس نمی‌شود نامش را روی جلد آورد.`,
    toCharacters: "شخصیت‌ها",
  },
  crop: {
    crop: "برش",
    title: "برش تصویر",
    hint: "قاب را بکشید تا جابه‌جا شود، یا یکی از گوشه‌هایش را تا اندازه‌اش عوض شود.",
    apply: "اعمال برش",
    applying: "در حال برش…",
    cancel: "انصراف",
    free: "آزاد",
    failed: "برش این تصویر انجام نشد.",
    lockedSquare: "چهره مربع است، تا هر صحنه‌ای که از آن کشیده می‌شود هم همین‌طور باشد.",
    lockedCover: "جلد عمودی است، تا ردیف‌های کتابخانه هم‌تراز بمانند.",
  },
  edition: {
    heading: "چاپ خوانندگان",
    published: (num, date) => `چاپ ${f(num)} · منتشرشده در ${date}`,
    notPublished: "هنوز منتشر نشده است. تا منتشر نکنید، خوانندگان چیزی نمی‌بینند.",
    upToDate: "خوانندگان همهٔ آنچه نوشته‌اید را دارند.",
    charactersChanged: "شخصیت‌ها از ویراست پیشین تغییر کرده‌اند.",
    coverChanged: "جلد از ویراست پیشین تغییر کرده است.",
    speakersChanged: (lines) => `گویندهٔ ${f(lines)} جمله از ویراست پیشین تغییر کرده است.`,
    speakersLost: (lines) =>
      `${f(lines)} جمله گوینده‌اش را از دست می‌دهد. پیش از انتشار، در «چه کسی هر جمله را می‌گوید» به‌روزرسانی را بزنید.`,
    picturesAdded: (c) => `${f(c)} تصویر افزوده`,
    picturesRemoved: (c) => `${f(c)} تصویر برداشته`,
    picturesEdited: (c) => `${f(c)} تصویر ویرایش‌شده`,
    changes: (c) => `${f(c)} تغییر که خوانندگان هنوز ندارند`,
    added: "فصل تازه",
    removed: "حذف‌شده",
    renamed: "نام تازه",
    moved: "جابه‌جا شده",
    text: "متن تغییر کرده",
    audio: "صدای تازه",
    noAudio: "بی‌صدا",
    audioOutdated: "صدا مال متن پیشین است",
    warning: (c) => `${f(c)} فصل بدون صدایی که با متنش بخواند منتشر می‌شود.`,
    note: "یادداشت برای خوانندگان (اختیاری)",
    notePlaceholder: "در این چاپ چه تغییر کرد",
    publish: (num) => `انتشار چاپ ${f(num)}`,
    publishing: "در حال انتشار…",
    publishFailed: "منتشر نشد.",
    readerLine: (num, date) => `چاپ ${f(num)} · ${date}`,
    whoCanRead: "چه کسی می‌تواند بخواند",
    everyone: "همه",
    onlyYou: "فقط شما",
    everyoneNote: "در بخش «مرور» برای همهٔ خوانندگانِ واردشده فهرست می‌شود.",
    onlyYouNote: "فقط خودتان می‌توانید آن را بخوانید، در اپ و این‌جا. در هیچ فهرستی نمی‌آید.",
    visibilityFailed: "تغییر نکرد. دوباره امتحان کنید.",
    groups: "گروه‌ها",
    groupsNote:
      "فقط کسانی که در گروه‌های انتخاب‌شده هستند می‌توانند آن را بخوانند. در هیچ فهرستی نمی‌آید.",
    noGroupChosen: "دست‌کم یک گروه انتخاب کنید، وگرنه جز خودتان کسی نمی‌تواند آن را بخواند.",
    noGroupsYet: "هنوز گروهی ندارید.",
    manageGroups: "مدیریت گروه‌ها",
  },
  library: {
    title: "کتابخانهٔ من",
    pickUp: "از همان‌جا که ماندید ادامه دهید",
    pickUpMenu: "گزینه‌های بیشتر برای این کتاب",
    removeFromPickUp: "برداشتن از «ادامه دهید»",
    removedFromPickUp: (title) => `«${title}» برداشته شد. جایی که ماندید می‌ماند.`,
    undo: "بازگرداندن",
    percentThrough: (p, title) => `${f(p)}٪ از ${title}`,
    chaptersFinished: (done, total) => `${f(done)} فصل از ${f(total)} فصل خوانده شده`,
    finishedSeal: (month) => `تمام شد · ${month}`,
    loadingBooks: "در حال بارگذاری کتاب‌ها",
    noBooks: "هنوز کتابی منتشر نشده است.",
    draft: "پیش‌نویس",
    onlyYou: "فقط شما",
    shared: "اشتراکی",
    sharedWithYou: "به اشتراک با شما",
    emptyTitle: "کتابخانه‌تان خالی است",
    emptyBody: "کتاب‌هایی که ذخیره می‌کنید و کتاب‌هایی که شنیدنشان را آغاز می‌کنید اینجا می‌مانند.",
    browseBooks: "دیدن همهٔ کتاب‌ها",
    save: "افزودن به کتابخانه",
    saved: "در کتابخانهٔ شما",
    remove: "برداشتن از کتابخانه",
    saveFailed: "ذخیره نشد. دوباره امتحان کنید.",
  },
  browse: {
    title: "همهٔ کتاب‌ها",
    allBooks: "همهٔ کتاب‌ها",
  },
  tag: {
    filter: "پالایش با برچسب",
    all: "همه",
    nothing: "هیچ کتابی اینجا این برچسب را ندارد.",
    tags: "برچسب‌ها",
    note: "واژه‌هایی که خواننده با آن‌ها کتاب‌ها را پالایش می‌کند. همهٔ کتابخانه‌ها یک فهرست برچسب دارند.",
    none: "هنوز برچسبی نیست.",
    remove: (name) => `برداشتن ${name}`,
    search: "یافتن یا ساختن برچسب",
    create: (name) => `برچسب تازهٔ «${name}»`,
    saveFailed: "برچسب‌ها ذخیره نشدند.",
    searchFailed: "جست‌وجوی برچسب‌ها انجام نشد.",
    link: "برچسب‌ها",
    title: "برچسب‌ها",
    what: "همهٔ برچسب‌ها، روی کتاب‌های همهٔ کتابخانه‌ها. تغییر نام یا ادغام، همهٔ کتاب‌های دارای آن را تغییر می‌دهد.",
    empty: "هنوز برچسبی نیست.",
    listFailed: "برچسب‌ها بار نشدند.",
    books: (count) => `${f(count)} کتاب`,
    rename: "تغییر نام",
    merge: "ادغام در…",
    mergeInto: (name) => `ادغام در ${name}`,
    mergeCancel: "انصراف",
    delete: "حذف",
    deleteQuestion: (name) => `«${name}» از همهٔ کتاب‌ها حذف شود؟`,
  },
  writer: {
    translatedBy: "ترجمهٔ",
    books: "کتاب‌ها",
    notFound: "نویسنده‌ای اینجا نیست، یا هیچ‌کدام از کتاب‌هایش برای شما نیست.",
    writers: "نویسندگان",
    writersNote:
      "چه کسی کتاب را نوشته و چه کسی ترجمه کرده است. خواننده با زدن نام، کتاب‌های دیگر آن نویسنده را می‌بیند.",
    none: "هنوز نویسنده‌ای نیست.",
    author: "نویسنده",
    translator: "مترجم",
    moveUp: "بالاتر",
    moveDown: "پایین‌تر",
    remove: "برداشتن",
    edit: "ویرایش",
    add: "افزودن نویسنده",
    addAs: "افزودن به‌عنوان",
    search: "جست‌وجوی نام",
    create: (name) => `نویسندهٔ تازه «${name}»`,
    sageleafNote: "برای ترجمهٔ ماشینی",
    saveFailed: "نویسندگان ذخیره نشدند.",
    searchFailed: "جست‌وجوی نویسندگان انجام نشد.",
    editTitle: "نویسنده",
    name: "نام",
    dates: "تاریخ‌ها",
    datesHint: "مثلاً: حدود ۵۰ – حدود ۱۳۵",
    bio: "درباره",
    save: "ذخیره",
    saved: "ذخیره شد",
    editFailed: "نویسنده ذخیره نشد.",
    shared:
      "کتاب‌های کتابخانه‌های دیگر هم این نویسنده را نام می‌برند، پس فقط مدیر مریم گلی می‌تواند آن را ویرایش کند.",
    backToBook: "بازگشت به کتاب",
    portrait: "چهره",
    uploadPortrait: "بارگذاری",
    drawPortrait: "کشیدن",
    drawNote: "از نام، تاریخ‌ها و درباره، به سبک خانه کشیده می‌شود — چند سنت.",
    portraitFailed: "چهره تنظیم نشد.",
  },
  plus: {
    badge: "پلاس",
    signInBadge: "ورود",
    openBadge: "همین حالا بخوانید",
    narratedHours: (hours) => `روایت‌شده · ${f(hours)} ساعت`,
    faces: (c) => `${f(c)} چهره`,
    pictures: (c) => `${f(c)} تصویر`,
    signInForPlus: "برای گرفتن پلاس وارد شوید",
    signInToRead: "برای خواندن وارد شوید",
    offerTitle: "این کتاب بخشی از پلاس است",
    offerBody: "با پلاس:",
    benefits: (book) => [
      "همهٔ کتاب‌های کتابخانه",
      ...(!book
        ? ["روایت صوتی در کتاب‌های روایت‌شده"]
        : book.narrated
          ? [
              book.voices
                ? "روایت‌شده، با صدایی برای هر شخصیت"
                : "روایت‌شده، برای شنیدن هم‌زمان با خواندن",
            ]
          : []),
      "تصویرها و چهرهٔ شخصیت‌ها در کتاب‌هایی که آن‌ها را دارند",
      "پرسیدن دربارهٔ هر سطر",
    ],
    getPlus: "دریافت پلاس",
    goodNews:
      "خبر خوب — دسترسی زودهنگام به پلاس را گرفتید، رایگان تا وقتی مریم گلی در دورهٔ آزمایشی است.",
    question: "وقتی پلاس پولی شود، آیا ماهی ۷٫۹۹ دلار برایتان می‌ارزد؟",
    choices: {
      yes: "بله",
      not_at_that_price: "نه با این قیمت",
      not_sure: "مطمئن نیستم",
    },
    noteLabel: "چه چیزی آن را ارزشمند می‌کند؟ (اختیاری)",
    pickToContinue: "برای ادامه یک پاسخ را انتخاب کنید.",
    continue: "ادامه به کتابخانهٔ کامل",
    terms:
      "ویژگی‌ها و دسترسی‌های دورهٔ آزمایشی ممکن است تغییر کنند؛ دست‌کم ۱۴ روز پیش از آنکه چیزی که استفاده می‌کنید پولی شود، خبر می‌دهیم.",
  },
  book: {
    backToStudio: "استودیو",
    backToLibrary: "کتابخانه",
    viewAsReader: "نمای خواننده",
    openInStudio: "باز کردن در استودیو",
    share: "اشتراک‌گذاری",
    linkCopied: "پیوند کپی شد",
    noChapters: "هنوز فصلی ندارد. نخستین فصل را پایین بچسبانید.",
    addHeading: "افزودن فصل",
    chapterTitle: "عنوان فصل",
    pasteText: "متن فصل را اینجا بچسبانید",
    chapterText: "متن فصل",
    addChapter: "افزودن فصل",
    addFailed: "فصل افزوده نشد.",
    tools: "ابزارهای کتاب",
    deleteNote: "حذف کتاب، آن و همهٔ محتوایش را پنهان می‌کند و راه برگشتی ندارد.",
    deleteBook: "حذف کتاب",
    deleteBookQuestion: (c) => `این کتاب و ${f(c)} فصلش حذف شود؟`,
    deleteFailed: "کتاب حذف نشد.",
    revision: (r) => ` · ویرایش ${f(r)}`,
    renameChapter: "تغییر نام فصل",
    moveChapter: "برای تغییر ترتیب بکشید، یا از کلیدهای جهت‌نما استفاده کنید",
    reorderFailed: "ترتیب تازه ذخیره نشد.",
    deleteChapter: "حذف فصل",
    deleteChapterQuestion: "این فصل حذف شود؟",
    bookNoun: "کتاب",
    chapterNoun: "فصل",
    language: "زبان",
    languageNote:
      "کتاب به چه زبانی نوشته شده است. از فصل اول تشخیص داده می‌شود و تعیین می‌کند هوش مصنوعی شخصیت‌ها، توصیف‌ها و متن تصویرها را به چه زبانی بنویسد.",
    languageUnset: "انتخاب نشده — از روی متن",
    languageFailed: "زبان تغییر نکرد.",
    story: "نوع کتاب",
    storyNote:
      "داستان، یا نامه، جستار و مانند آن. پاسخ‌ها داستان را داستان می‌خوانند و می‌توانند فصل‌های بعدش را لو ندهند؛ کتاب‌های دیگر را به‌خاطر آنچه می‌گویند می‌خوانند و پرسش درباره‌شان همیشه «آزاد» است.",
    storyYes: "داستان",
    storyNo: "داستان نیست",
    storyFailed: "نوع کتاب تغییر نکرد.",
    pictureMedium: "سبک تصویرها",
    pictureMediumNote:
      "چند واژه که همهٔ تصویرها و جلد این کتاب با آن کشیده می‌شوند. تغییرش رایگان است و چیزی را دوباره نمی‌کشد: برای تصویرهایی است که از این پس توصیف می‌شوند. چهره‌ها سبک خودشان را نگه می‌دارند.",
    pictureMediumPlaceholder: "با نخستین تصویر انتخاب می‌شود",
    pictureMediumClear: "با تصویر بعدی انتخاب شود",
    pictureMediumTooLong: (max) => `کمتر از ${f(max)} نویسه بنویسید.`,
    pictureMediumFailed: "سبک تصویرها ذخیره نشد.",
    editList: "ویرایش فهرست",
    doneEditing: "تمام",
    stateNarrating: "در حال روایت…",
    stateNotNarrated: "روایت‌نشده",
    stateAudioOutdated: "صدا از متن عقب است",
    stateNarrated: "روایت‌شده",
    stateNarrationFailed: "روایت ناموفق بود",
    stateMissing: (c) => `${f(c)} تکه بی‌صدا`,
    publishLineUnpublished: "در ویراست منتشرشده نیست.",
    publishLineOutdated: "خوانندگان نسخهٔ قدیمی‌تر این فصل را دارند.",
    publishLineLink: "انتشار",
    summaryChapters: (c) => `${f(c)} فصل`,
    length: (minutes) =>
      minutes >= 60
        ? `${f(Math.floor(minutes / 60))} ساعت` +
          (minutes % 60 ? ` و ${f(minutes % 60)} دقیقه` : "")
        : `${f(minutes)} دقیقه`,
    minutesLeft: (minutes) => `${f(minutes)} دقیقه مانده`,
    startListening: "شروع شنیدن",
    startReading: "شروع خواندن",
    sep: "، ",
    continueListening: "ادامه",
    listenAgain: "شنیدن دوباره",
    readAgain: "خواندن دوباره",
    chaptersHeading: "فصل‌ها",
    finished: "تمام‌شده",
    more: "بیشتر",
    less: "کمتر",
    summaryNotNarrated: (c) => `${f(c)} روایت‌نشده`,
    summaryOutdated: (c) => `صدا در ${f(c)} فصل از متن عقب است`,
    summaryNarrating: (c) => `${f(c)} در حال روایت`,
    summaryToPublish: (c) => `${f(c)} تغییر برای انتشار`,
    summaryAllDone: "روایت و منتشر شده",
  },
  title: {
    edit: (noun) => `ویرایش ${noun}`,
    rename: (noun) => `تغییر نام این ${noun}`,
    renameFailed: "نام تغییر نکرد.",
  },
  file: {
    tooLarge: "این فایل برای یک فصل بیش از حد بزرگ است.",
    noText: "این فایل متنی ندارد.",
    tooLong: (c, max) =>
      `این فایل ${f(c)} نویسه دارد و هر فصل حداکثر ${f(max)} نویسه می‌پذیرد؛ اول آن را تقسیم کنید.`,
    loaded: (name, c) => `${name} بارگذاری شد · ${f(c)} نویسه`,
    loadedAs: (name, encoding, c) => `${name} با کدگذاری ${encoding} بارگذاری شد · ${f(c)} نویسه`,
    choose: "انتخاب فایل",
    orDrop: "یا فایل txt، md یا epub را اینجا رها کنید",
    reading: (name) => `در حال خواندن ${name}…`,
    readFailed: "این فایل EPUB خوانده نشد.",
    found: (name, c) => `${name} · ${f(c)} فصل. هر فصلی را که نمی‌خواهید از انتخاب بیرون بیاورید.`,
    untitled: (position) => `فصل ${f(position)}`,
    tooLongToAdd: (max) => `برای افزودن بیش از حد بلند است؛ هر فصل حداکثر ${f(max)} نویسه دارد`,
    addChapters: (c) => `افزودن ${f(c)} فصل`,
    addFailed: "فصل‌ها افزوده نشدند.",
    another: "انتخاب فایلی دیگر",
  },
  voice: {
    label: "صدا · ",
    theDefault: " (پیش‌فرض)",
    designed: "طراحی‌شده برای این کتاب",
    everySample: "همهٔ نمونه‌ها این جمله را می‌خوانند:",
    loadFailed: "فهرست صداها بارگذاری نشد.",
    playSample: (name) => `پخش نمونهٔ ${name}`,
    modelLabel: "مدل گفتار",
    models: {
      "gemini-3.8-flash-tts": "جمینای ۳٫۸ فلش",
      "gemini-3.8-flash-lite-tts": "جمینای ۳٫۸ فلش لایت",
      "gemini-3.1-flash-tts-preview": "جمینای ۳٫۱ فلش",
      "gemini-2.5-pro-preview-tts": "جمینای ۲٫۵ پرو",
      "tts-rt-v2": "سونیوکس",
      eleven_v4: "الون‌لبز ۴",
      eleven_v4_turbo: "الون‌لبز ۴ توربو",
      eleven_v3: "الون‌لبز ۳",
      "qwen3-tts-1.7b": "کوئن۳-TTS",
      "kokoro-82m": "کوکورو",
    },
    fixedVoice:
      "تنها صدایی که این مدل با آن می‌خواند. انتخاب این مدل خودِ انتخاب است و چیزی برای شنیدن نیست.",
    directionLabel: "چگونه خوانده شود",
    directionPlaceholder: "آرام و بی‌شتاب، مثل راوی کتاب صوتی",
    directionNote:
      "به‌عنوان دستور به راوی فرستاده می‌شود و هرگز خوانده نمی‌شود. تغییرش فصل‌هایی را که از این پس روایت کنید دوباره ضبط می‌کند؛ آنچه ضبط شده دست‌نخورده می‌ماند.",
    directionTooLong: (max) => `کمتر از ${f(max)} نویسه بنویسید.`,
    directionClear: "بدون دستور",
    directionSuggestions: [
      "آرام و بی‌شتاب، مثل راوی کتاب صوتی",
      "گرم و نزدیک، انگار برای یک نفر می‌خوانید",
      "تند و روشن",
      "سنگین و شمرده",
    ],
    partSizeLabel: "اندازهٔ بخش‌ها",
    partSizeChip: (chars) => f(chars),
    partSizeOption: (chars, seconds) => `${f(chars)} نویسه · حدود ${f(seconds)} ثانیه`,
    partSizeDefault: " (پیش‌فرض)",
    partSizeCapped: (max) =>
      `در این مدل بخش‌ها حداکثر ${f(max)} نویسه‌اند تا هیچ‌کدام نیمه‌کاره نماند.`,
    readLabel: "هر بخش از جملهٔ پیش از آن ضبط شود و آن جمله سپس بریده شود",
    readNote: "پیوند بخش‌ها نرم‌تر می‌شود. کمی گران‌تر است و فقط در حالت سریع کار می‌کند.",
  },
  voices: {
    open: "صدا برای هر شخصیت",
    openNote: "برای راوی و هر شخصیتی که حرف می‌زند صدایی طراحی کنید.",
    note: "هر صدا از روی توصیفش طراحی می‌شود و با جمله‌های خودش از کتاب شنیده می‌شود. از دو صدا یکی را برگزینید؛ بعداً هم می‌توانید عوضش کنید.",
    notOffered: "صدا برای هر شخصیت فقط برای کتاب‌های انگلیسی با ElevenLabs v4 در دسترس است.",
    loadFailed: "فهرست صداها بار نشد.",
    narrator: "راوی",
    lines: (count) => `${f(count)} جمله`,
    chosen: "صدای برگزیده",
    playChosen: "پخش صدای برگزیده",
    noVoice: "هنوز صدایی ندارد: راوی این جمله‌ها را می‌خواند.",
    narratorStock: "هنوز صدای طراحی‌شده‌ای ندارد: صدای کتاب می‌خواند.",
    description: "توصیف صدا",
    reads: "می‌خواند:",
    design: (amount) => `طراحی دو صدا · ${amount}`,
    designAgain: (amount) => `طراحی دو صدای دیگر · ${amount}`,
    designing: "در حال طراحی…",
    candidate: (i) => `صدای ${f(i)}`,
    playCandidate: (i) => `پخش صدای ${f(i)}`,
    choose: "برگزیدن",
    choosing: "در حال ذخیره…",
    failed: "نشد. دوباره امتحان کنید.",
    useExisting: "استفاده از صدایی موجود",
    hideExisting: "پنهان کردن صداهای موجود",
    existingNote: "صدایی که از پیش در حساب سرویس صدا هست. برگزیدنش هزینه‌ای ندارد.",
    sample: (amount) => `شنیدن این جمله‌ها با این صدا · ${amount}`,
    sampling: "در حال خواندن…",
    playsSample: "همین صدا را هنگام خواندن جمله‌های این نقش پخش می‌کند.",
    playsPreview: "پیش‌شنیدِ خودِ سازنده را پخش می‌کند، نه جمله‌های این نقش را.",
    slots: (used, limit) => `جای صدا ${f(used)} از ${f(limit)}`,
    noAccountVoices: "حساب هنوز صدایی از خودش ندارد.",
    voiceID: "شناسهٔ صدا",
    voiceLabel: "نام (اختیاری)",
    useVoice: "استفاده از این صدا",
    using: "در حال بررسی…",
    findLibrary: "جست‌وجو در کتابخانهٔ صداها",
    hideLibrary: "پنهان کردن صداهای کتابخانه",
    libraryNote:
      "صداهایی که دیگران ساخته‌اند، یافته از روی توصیف. پیش‌شنیدن رایگان است و برگزیدن هم: صدا به حساب سرویس صدا افزوده می‌شود — جای صدایی نمی‌گیرد، ولی یکی از افزودن‌های این ماه است.",
    libraryAdds: (used, limit) => `افزودن صدا در این ماه ${f(used)} از ${f(limit)}`,
    libraryWithdraw: "صدای کتابخانه از آنِ سازندهٔ آن است که می‌تواند با اطلاع پیشین پسش بگیرد.",
    libraryNotice: (days) => `اطلاع ${f(days)} روز`,
    libraryInAccount: "در حساب",
    libraryHQ: "کیفیت بالا",
    libraryHQTitle: "کیفیت بالا، به نشانِ خودِ سرویس صدا",
    libraryRelaxed: "نتیجهٔ دقیق کم بود، پس جست‌وجو گسترده‌تر شد.",
    accent: "لهجه",
    accentNote:
      "لهجه‌ای که صداهای این کتاب با آن حرف می‌زنند. توصیفی که خودش لهجه‌ای را نام ببرد همان را نگه می‌دارد.",
    accentFailed: "لهجه عوض نشد.",
    accentName: (accent) =>
      ({
        american: "آمریکایی",
        british: "بریتانیایی",
        australian: "استرالیایی",
        irish: "ایرلندی",
        scottish: "اسکاتلندی",
      })[accent] ?? accent,
    libraryNone: "چیزی در کتابخانه نمی‌خورد. واژه‌های دیگری در توصیف بیازمایید.",
    librarySearchAgain: "جست‌وجو با این توصیف",
    libraryPreview: (name) => `پخش ${name}`,
    libraryChosen: "از کتابخانهٔ صداها: سازنده‌اش می‌تواند پسش بگیرد.",
    chosenNamed: (label) => `صدای برگزیده · ${label}`,
    needsVoice: (count) => `بی‌صدا · ${f(count)}`,
    hasVoice: (count) => `دارای صدا · ${f(count)}`,
    extrasTitle: "صداهای فرعی",
    extrasNote:
      "برای کسانی که کتاب آن‌ها را شخصیت نمی‌شمارد — فرستاده‌ای، نگهبانی، ریش‌سفیدی. آماده‌سازی فصل برای صداها برای هر سخن آن‌ها یکی از این‌ها را برمی‌گزیند، و هر گونه همین که سخنی یافت اینجا می‌آید. گونه‌ای که صدا ندارد را راوی می‌خواند.",
    extra: {
      older_man: "مرد سالخورده",
      younger_man: "مرد جوان",
      older_woman: "زن سالخورده",
      younger_woman: "زن جوان",
      child: "کودک",
    },
  },
  script: {
    multiSpeakerLabel: "صدایی برای هر شخصیت",
    multiSpeakerNote:
      "سخنان هر شخصیت با صدای خودش خوانده می‌شود، از روی متنی که فصل به فصل آماده می‌شود. برای کتاب‌های انگلیسی با ElevenLabs v4.",
    title: "آماده‌سازی برای صداها",
    note: "پیش از هر سخن شخصیت‌ها یک نشانهٔ کوتاه اجرا و در هر لحظهٔ مهم داستان یک مکث می‌گذارد، در یک گذر بر کل فصل. متن شما همان می‌ماند: فقط راوی و صداها نشانه‌ها را می‌شنوند.",
    prepare: "آماده‌سازی برای صداها",
    notStarted: "هنوز آماده نشده",
    preparing: (done, total) => `در حال آماده‌سازی… ${f(done)} از ${f(total)} بند`,
    summary: (cues, pauses) => `${f(cues)} نشانه، ${f(pauses)} مکث`,
    prepareRest: "آماده‌سازی باقی بندها",
    retry: "تلاش دوباره برای بندهای ناموفق",
    failed: (k) => `${f(k)} بند آماده نشد:`,
    notes: "برخی نشانه‌ها به کار نرفت:",
    clear: "حذف متن اجرا",
    narrator: "راوی",
    someoneElse: "کسی دیگر",
    notSpeech: "سخن نیست",
    unknown: "هنوز گوینده‌اش معلوم نشده",
    cueLabel: "این سخن چگونه گفته می‌شود",
    cuePlaceholder: "افزودن نشانه",
    pause: "مکث",
    longPause: "مکث بلند",
    addPause: "مکث پس از این بند",
    pauseCycle: "برای تغییر مکث بزنید",
    speakerLabel: "گویندهٔ این سخن",
    edited: "ویرایش‌شده",
    saveFailed: "این تغییر ذخیره نشد.",
    extraLabel: "کدام صدای فرعی می‌خواندش",
    extraNote: "همهٔ سخنان این گوینده در فصل عوض می‌شود.",
    extraUncast: "هنوز صدایی برای این گونه برگزیده نشده: راوی می‌خواندش.",
    nameLabel: "چه کسی سخن می‌گوید",
    namePlaceholder: "نام گوینده",
    unnamed: (k) => `${f(k)} سخن از کسی دیگر که گوینده‌اش نام ندارد`,
  },
  narration: {
    backToBook: "کتاب",
    read: "پیش‌نمایش خواننده",
    textChanged: "متن یا اصلاح‌هایش پس از روایت تغییر کرده است.",
    performedChanged: "متن، متن اجرای صداها یا صدای یکی از شخصیت‌ها پس از روایت تغییر کرده است.",
    performedWith: (voices) =>
      `با صدایی برای هر شخصیت روایت می‌شود: ${voices}. بقیه را راوی می‌خواند.`,
    performedLines: (name, lines) => `${name} (${f(lines)} سطر)`,
    performedNoVoices:
      "از متن اجرای صداها روایت می‌شود؛ هنوز هیچ شخصیتی در این فصل صدا ندارد، پس راوی همه را می‌خواند.",
    onlyRerecorded: (k, total) => ` فقط ${f(k)} از ${f(total)} بخش دوباره ضبط می‌شود.`,
    levelsChanged:
      "بلندی صداها را می‌توان یکدست کرد: روایت دوباره، بخش‌های ضبط‌شده را بی‌هزینه هم‌تراز می‌کند.",
    relevelled: (c) => `${f(c)} بخش هم‌تراز می‌شود، رایگان`,
    parts: (c) => `${f(c)} بخش`,
    alreadyNarrated: (c) => `${f(c)} بخش از قبل روایت شده`,
    voiceChanged: (from, to, c) =>
      `این فصل با صدای ${from} روایت شده است. روایت با ${to} هر ${f(c)} بخش را با قیمت کامل دوباره ضبط می‌کند.`,
    modelChanged: (c) =>
      `این فصل با مدل گفتار دیگری روایت شده است. روایت دوباره هر ${f(c)} بخش را با قیمت کامل دوباره ضبط می‌کند.`,
    economy: "اقتصادی — نصف قیمت",
    whatIsEconomy: "حالت اقتصادی چیست؟",
    economyExplainer:
      "همان صدا با نصف قیمت، که وقتی نوبتش نزد سرویس برسد ساخته می‌شود، نه همان لحظه — برای فصل کوتاه حدود چهار دقیقه و برای فصل بلند بیش از یک ساعت طول کشیده است. می‌توانید صفحه را ببندید؛ کار ادامه می‌یابد.",
    narrate: "روایت",
    renarrate: "روایت دوباره",
    recordAgain: "ضبط دوباره",
    recordAgainTitle: (c) =>
      `هر ${f(c)} بخش با همان متن، متن اجرا و صداها از نو و با قیمت کامل ضبط می‌شود.`,
    settings: "تنظیمات روایت",
    settingsNote:
      "صدا، مدل گفتار و اندازهٔ بخش‌ها از آنِ کتاب است: همهٔ فصل‌ها با همین‌ها روایت می‌شوند.",
    settingsSummary: (voice, model, chars) => `${voice} · ${model} · بخش‌های ${f(chars)} نویسه‌ای`,
    prepareNotStarted: "آماده نشده",
    prepareFirst: "اول متن آماده شود",
    preparingFirst: (done, total) => `آماده‌سازی متن، ${f(done)} از ${f(total)} قطعه، سپس روایت…`,
    narrateAll: "روایت همه",
    narrateAllEstimate: (chapters, cost) => `${f(chapters)} فصل روایت می‌خواهد · حدود ${cost}`,
    narrateAllNothing: "همهٔ فصل‌ها روایت شده‌اند.",
    narrateAllStarted: (c) => `روایت ${f(c)} فصل…`,
    narrateAllFailed: "روایت شروع نشد.",
    problemsLine: (passages, chapters) => `روایت · ${f(passages)} تکه بی‌صدا در ${f(chapters)} فصل`,
    problemsNone: "همهٔ تکه‌های روایت‌شده صدا دارند.",
    problemsNote:
      "تکه‌هایی که روایت برایشان صدایی ندارد. باقی هر فصل پخش می‌شود؛ این‌ها را درست کنید و فصل را دوباره روایت کنید تا فقط همین‌ها ضبط شوند.",
    problemsChapterMissing: (c) => `${f(c)} تکه بی‌صدا`,
    problemsChapterFailed: "هیچ بخشی از این فصل صدا ندارد",
    problemCause: {
      unsaid_run: "بررسی، واژه‌های نشان‌داده‌شده را در ضبط نشنید",
      stopped_early: "ضبط پیش از پایان تکه قطع شد",
      lead_in_miss: "پیوند با تکهٔ پیشین درست درنیامد",
      refused: "مدل گفتار از خواندن این تکه سر باز زد",
      account: "حساب سرویس گفتار نتوانست این را انجام دهد: کلید یا موجودی را بررسی کنید",
      provider_error: "سرویس گفتار خطا داد",
      timed_out: "بیش از حد طول کشید و رها شد",
      unknown: "ناموفق بود",
      cue_spoken: "ضبط، راهنمای اجرا را به‌جای اجرا کردن بلند خواند",
      voice_gone: "صدایی که برای این تکه برگزیده شده دیگر در سرویس گفتار نیست",
      extra_speech: "ضبط واژه‌هایی می‌گوید که در متن نیست",
    },
    problemSkippedSomewhere: "بررسی، واژه‌هایی از این تکه را در ضبط نشنید",
    problemNoRetry: {
      unsaid_run:
        "روایت دوباره همان ضبط را می‌دهد. واژه‌هایی که راوی نمی‌تواند بخواند، مانند خطی دیگر یا نمادها، باید ویرایش شوند. اگر نام‌اند، شاید بررسی آن‌ها را اشتباه شنیده باشد: تلفظ فقط به نامی کمک می‌کند که شنیده‌اید نادرست گفته شده.",
      refused: "تلاش دوباره همان نتیجه را می‌دهد: متن را ویرایش کنید.",
      voice_gone: "تلاش دوباره هم این صدا را نمی‌یابد: صدای دیگری برای این نقش برگزینید.",
    },
    problemShowInText: "نمایش در متن",
    problemTryAgain: (c) => (c === 1 ? "دوباره امتحان کنید" : `دوباره امتحان ${f(c)} تکه`),
    problemDetails: "جزئیات",
    problemsFailed: "تکه‌های نیازمند رسیدگی بارگیری نشد.",
    narrateAllTitle: "این فصل‌ها روایت شوند؟",
    narrateAllNotNarrated: (c) => `${f(c)} فصل هنوز روایت نشده`,
    narrateAllTextChanged: (c) => `${f(c)} فصل با متن تغییریافته`,
    narrateAllRefinish: (c) => `${f(c)} فصل برای هم‌ترازی صداها، رایگان`,
    narrateAllRevoiced: (c) => `${f(c)} فصل با همان متن، با اجرای دیگری`,
    narrateAllRecordedOn: (c, model, voice) => `${f(c)} فصل ضبط‌شده با ${model} · ${voice}`,
    narrateAllRecordedWith: (c, settings) => `${f(c)} فصل ضبط‌شده با ${settings} متفاوت`,
    narrateAllSetting: {
      model: "مدل گفتار",
      voice: "صدا",
      part_size: "اندازهٔ بخش",
      read_context: "پیش‌خوانی",
      direction: "راهنمای لحن",
      language: "زبان",
    },
    narrateAllSettingSeparator: " و ",
    narrateAllRedoNote: (model, voice) =>
      `گروه‌های انتخاب‌شده با تنظیمات کتاب، ${model} · ${voice}، از نو و به‌طور کامل ضبط می‌شوند.`,
    narrateAllConfirm: (c) => `روایت ${f(c)} فصل · حدود`,
    narrateAllCancel: "انصراف",
    narrateAllRunning: (done, planned) => `شروع فصل‌ها، ${f(done)} از ${f(planned)}…`,
    narrateAllDone: (started, notStarted) =>
      `روایت ${f(started)} فصل شروع شد.` +
      (notStarted > 0 ? ` ${f(notStarted)} فصل شروع نشد.` : ""),
    narrateAllStoppedBudget: (started, planned) =>
      `پس از ${f(started)} از ${f(planned)} فصل متوقف شد: بودجه تمام شد.`,
    narrateAllStoppedNoBudget: "متوقف شد: این کتابخانه سقف هزینه ندارد.",
    narrateAllRunFailed: (started, planned) =>
      `پس از ${f(started)} از ${f(planned)} فصل متوقف شد. برای ادامه «روایت همه» را بزنید.`,
    prepareSummary: (marks) => `${f(marks)} نشانه افزوده شد`,
    prepareTitle: "آماده‌سازی متن برای روایت",
    prepareNote:
      "کسرهٔ اضافه و ویرگول را جایی که خوانده می‌شوند ولی نوشته نشده‌اند، بخش به بخش می‌افزاید. هیچ چیز دیگری نباید تغییر کند و متن شما همان‌طور می‌ماند: این نشانه‌ها را فقط راوی می‌شنود.",
    prepare: "آماده‌سازی متن",
    preparing: (done, total) => `در حال آماده‌سازی… ${f(done)} از ${f(total)} بخش`,
    prepared: (marks, done, total) =>
      `${f(marks)} نشانه در ${f(done)} از ${f(total)} بخش افزوده شد. برای شنیدنشان روایت کنید.`,
    prepareRest: "آماده‌سازی باقی بخش‌ها",
    prepareRetry: "تلاش دوباره برای بخش‌های ناموفق",
    prepareFailed: (k) => `${f(k)} بخش آماده نشد:`,
    prepareNotes: "بخشی از متن بدون نشانه ماند:",
    reviewMarks: "بازبینی نشانه‌ها",
    hideMarks: "پنهان کردن نشانه‌ها",
    reviewNote: "برای برداشتن نشانهٔ افزوده‌شده روی واژهٔ پررنگ بزنید.",
    clearPreparation: "حذف آماده‌سازی",
    startFailed: "روایت شروع نشد.",
    narrating: (done, total) => `در حال روایت ${f(done)} از ${f(total)}…`,
    economyWaiting: " در صف نرخ اقتصادی — ممکن است یک ساعت یا بیشتر طول بکشد.",
    stop: "توقف",
    stopFailed: "این روایت متوقف نشد.",
    prepareStillRunning:
      "متن هنوز در حال آماده‌سازی است. چیزی روایت نشد — بخش آماده‌سازی را باز کنید تا ببینید کجا مانده، بعد دوباره امتحان کنید.",
    settingChangeFailed: "این تنظیم ذخیره نشد.",
    cancelled: "متوقف شد. بخش‌های روایت‌شده می‌مانند و «روایت» بقیه را ادامه می‌دهد.",
    finished: "تمام شد",
    finishedPartial: "تمام شد، اما برخی تکه‌ها صدا ندارند",
    partsFailed: (c) => `${f(c)} تکه صدا ندارد. بقیهٔ فصل پخش می‌شود.`,
    failed: (reason) => `روایت ناموفق بود${reason ? `: ${reason}` : "."} دوباره امتحان کنید.`,
    noAudio: "این فصل هنوز صدا ندارد.",
    audioOutdated:
      "این متن کنونی است. صدا از نسخهٔ پیشین است؛ برای شنیدن همین نسخه دوباره روایت کنید.",
    playEarlierAudio: "پخش صدای پیشین",
    playingEarlier: "در حال پخش صدای نسخهٔ پیشین، همراه متن همان نسخه.",
    showCurrentText: "نمایش متن کنونی",
  },
  fixes: {
    heading: "اصلاح‌ها",
    open: (c) => `${f(c)} اصلاح`,
    detached: (c) =>
      `${f(c)} اصلاح دیگر با متن جور نیست و نادیده گرفته می‌شود. واژه را دوباره لمس طولانی کنید تا دوباره وصل شود، یا حذفش کنید.`,
    deleteFor: (word) => `حذف اصلاح ${word}`,
    deleteFailed: "این اصلاح حذف نشد و هنوز اعمال می‌شود.",
    scopes: {
      skipped: "نادیده",
      overridden: "بی‌اثر",
      occurrence: "یک واژه",
      chapter: "فصل",
      book: "کتاب",
      org: "کتابخانه",
    },
  },
  search: {
    heading: "جستجو در متن این کتاب",
    on: "پرسش‌های خوانندگان از هر آنچه تا این‌جا خوانده‌اند پاسخ می‌گیرد، همراه با نقل‌قول‌هایی که با لمسشان به همان‌جا می‌روند. از ادامهٔ کتاب چیزی جستجو نمی‌شود.",
    off: "خوانندگان می‌توانند بپرسند و پاسخ از همان صفحه‌ای می‌آید که در آن هستند. با روشن کردن این گزینه، پاسخ از هر آنچه تا این‌جا خوانده‌اند و همراه با نقل‌قول می‌آید.",
    working: "در حال انجام…",
    turnOn: "روشن کردن",
    turnOff: "خاموش کردن",
    indexed: (i, total) => `${f(i)} از ${f(total)} فصل قابل جستجوست`,
    stale: (c, cost) => `${f(c)} فصل قدیمی است — به‌روزرسانی با ${cost}`,
    estimate: (cost) => `نمایه‌سازی یک بار حدود ${cost} هزینه دارد.`,
    pending: "در حال نمایه‌سازی — حدود یک دقیقه طول می‌کشد.",
    changeFailed: "تنظیم جستجو تغییر نکرد.",
  },
  studio: {
    title: "استودیو",
    forbidden:
      "ساختن کتاب برای این حساب روشن نیست. بقیه‌اش هست: کتابخانه هر کتابی را که بتوانید باز کنید می‌خواند، پخش می‌کند و به پرسش‌ها دربارهٔ آن پاسخ می‌دهد.",
    backToLibrary: "بازگشت به کتابخانه",
    newBookTitle: "عنوان کتاب تازه",
    newBookLanguage: "به چه زبانی نوشته شده است؟",
    newBook: "کتاب تازه",
    create: "ساختن",
    createFailed: "کتاب ساخته نشد.",
    empty: "هنوز کتابی نیست. کتابی تازه بسازید و فصل‌هایش را بیفزایید.",
    publish: "انتشار",
    unpublish: "لغو انتشار",
    groups: "گروه‌ها",
    tools: {
      publish: "انتشار",
      characters: "شخصیت‌ها",
      cover: "جلد",
      settings: "تنظیمات",
      narration: "نیازمند رسیدگی",
      voices: "صداها",
    },
    toolCoverSet: "کتاب جلد دارد",
    toolCoverNone: "هنوز جلدی ندارد",
    toolPublishNone: "هنوز منتشر نشده",
    toolPublishEdition: (edition, changes) =>
      changes === 0
        ? `ویراست ${f(edition)} · به‌روز`
        : `ویراست ${f(edition)} · ${f(changes)} تغییر برای انتشار`,
    toolPublishFirst: (changes) => `${f(changes)} فصل برای انتشار`,
    toolCharactersNone: "هنوز استخراج نشده",
    toolCharactersCount: (c) => `${f(c)} شخصیت`,
    toolSettingsSearchOn: "جستجو روشن",
    toolSettingsSearchOff: "جستجو خاموش",
    toolCharactersStale: (c) => `${f(c)} فصل از آن زمان تغییر کرده`,
    toolSearchStale: (c) => `${f(c)} فصل قدیمی است`,
  },
  groups: {
    title: "گروه‌ها",
    intro:
      "گروه فهرستی از آدم‌هاست. کتابِ منتشرشده را از صفحهٔ انتشارش با یک گروه به اشتراک بگذارید تا فقط آن‌ها بتوانند بخوانندش.",
    newGroup: "گروه تازه",
    newGroupName: "نام گروه",
    create: "ساختن",
    rename: "تغییر نام",
    save: "ذخیره",
    cancel: "انصراف",
    delete: "حذف گروه",
    confirmDelete: (name) =>
      `«${name}» حذف شود؟ کتاب‌هایی که با آن به اشتراک گذاشته شده‌اند دیگر برای این افراد نیستند.`,
    empty: "هنوز گروهی نیست.",
    people: (c) => `${f(c)} نفر`,
    nobody: "هنوز کسی در این گروه نیست.",
    addPerson: "افزودن",
    addPersonEmail: "ایمیلی که با آن وارد شده‌اند",
    remove: "برداشتن",
    failed: "انجام نشد. دوباره امتحان کنید.",
    forbidden: "فقط سازندگان این کتابخانه می‌توانند گروه‌هایش را مدیریت کنند.",
  },
  translation: {
    blank: "خالی",
    translate: "ترجمهٔ یک کتاب",
    blankNote: "کتابی تازه و خالی. پس از آن فصل‌هایش را بیفزایید.",
    translateNote:
      "نسخه‌ای از یکی از کتاب‌هایتان به زبانی دیگر: متن، شخصیت‌ها و توصیف‌ها ترجمه می‌شوند و جلد، چهره‌ها و تصویرها دوباره به کار می‌روند. چیزی روایت نمی‌شود؛ صدای زبان تازه را بعد انتخاب می‌کنید.",
    from: "ترجمهٔ",
    into: "به",
    noSources: "هنوز هیچ‌یک از کتاب‌هایتان هم زبان و هم متن ندارد.",
    estimate: (chapters, cost) => `${f(chapters)} فصل، حدود ${cost}`,
    estimating: "در حال برآورد هزینه…",
    start: (cost) => `ترجمه، حدود ${cost}`,
    startFailed: "ترجمه آغاز نشد.",
    running: (done, total) => `در حال ترجمه، ${f(done)} از ${f(total)} فصل`,
    paused: "ترجمه متوقف شده است",
    done: (source) => `ترجمه‌شده از ${source}`,
    cancelled: "ترجمه لغو شد",
    spent: (cost) => `تاکنون ${cost}`,
    spentTotal: (cost) => `روی هم ${cost}`,
    background:
      "ترجمه در پس‌زمینه ادامه می‌یابد و می‌توانید برنامه را ببندید. فصل‌ها با تمام شدن ظاهر می‌شوند.",
    held: "وقتی ترجمه تمام یا لغو شود می‌توانید فصل بیفزایید.",
    reasons: {
      refused: "مدل از ترجمهٔ این متن خودداری کرد.",
      failed: "سرویس ترجمه پیاپی خطا داد.",
      budget: "بودجهٔ این ماه کتابخانه تمام شده است. آن را افزایش دهید و دوباره بکوشید.",
      too_long: "فصل ترجمه‌شده از بیشینهٔ طول یک فصل بلندتر است.",
      invalid: "ترجمه به‌صورت فصل ذخیره نشد.",
      source_gone: "کتابی که از آن ترجمه می‌شود حذف شده است.",
    },
    reasonAt: (chapter, reason) => (chapter > 0 ? `فصل ${f(chapter)}: ${reason}` : reason),
    retry: "تلاش دوباره",
    retryFailed: "تلاش دوباره انجام نشد.",
    cancel: "توقف ترجمه",
    cancelQuestion: "برای همیشه متوقف شود؟ فصل‌های ترجمه‌شده می‌مانند.",
    cancelFailed: "ترجمه متوقف نشد.",
    estimatedPictures: (c) => `جای ${f(c)} تصویر تخمینی است؛ جایشان را بررسی کنید:`,
    summaryRunning: (done, total) => `در حال ترجمه، ${f(done)} از ${f(total)}`,
    summaryPaused: "ترجمه متوقف شده",
    kind: "سپس",
    copyKind: "نسخه‌ای که خودم ویرایش کنم",
    copyNote: "از همان آغاز از آنِ شماست. کتاب اصلی ممکن است تغییر کند و این دنبالش نمی‌رود.",
    linkedKind: "پیوسته به کتاب اصلی",
    linkedNote:
      "ترجمهٔ همین کتاب می‌ماند: واژه‌ها، شخصیت‌ها و تصویرهایش از کتاب اصلی می‌آیند و با تغییر آن، تازه‌اش می‌کنید. آن‌ها را دستی نمی‌توانید ویرایش کنید — ولی مثل هر کتاب دیگری روایت و منتشرش می‌کنید.",
    linkedTo: (source) => `پیوسته به ${source}`,
    linkedBlurb: "متن، شخصیت‌ها و تصویرهایش از کتاب اصلی می‌آیند. برای گرفتن تغییرها تازه‌اش کنید.",
    checking: "در جست‌وجوی تغییرها…",
    upToDate: "با کتاب اصلی هم‌گام است.",
    refresh: "تازه‌سازی",
    refreshWithCost: (cost) => `تازه‌سازی، حدود ${cost}`,
    refreshFailed: "تازه‌سازی انجام نشد.",
    refreshing: "در حال تازه‌سازی…",
    changeDetails: "مشخصات کتاب یا شخصیت‌هایش",
    changeChapters: (c) => `${f(c)} فصل تغییر کرده`,
    changeAdded: (c) => `${f(c)} فصل افزوده`,
    changeRemoved: (c) => `${f(c)} فصل حذف‌شده`,
    changePictures: (c) => `تصویرهای ${f(c)} فصل — رایگان، و روایت حفظ می‌شود`,
    unlink: "گسستن پیوند",
    unlinkQuestion: "پیوند برای همیشه گسسته شود؟ کتاب از آنِ شما می‌شود و دیگر تازه نمی‌شود.",
    unlinkFailed: "پیوند گسسته نشد.",
    lockedNote:
      "این کتاب به کتابی دیگر پیوسته است و محتوایش از آن می‌آید. برای ویرایش دستی، پیوند را بگسلید.",
    howHeading: "شیوهٔ ترجمه",
    howNote:
      "این همان چیزی است که به مدل گفته می‌شود کتاب چگونه باید خوانده شود. برای لحنی دیگر — ساده‌تر، کهن‌تر، رسمی‌تر — تغییرش دهید.",
    howReset: "بازگرداندن حالت پیش‌فرض",
    rulesNote:
      "این قاعده‌ها هر بار همراه آن می‌روند و تغییرپذیر نیستند؛ همان‌هایند که پاسخ را قابل‌استفاده می‌کنند. پاسخی که آن‌ها را بشکند، هزینه‌اش پرداخت و خودش دور ریخته می‌شود.",
    tryIt: "آزمایش روی یک بند",
    tryAnother: "بندی دیگر",
    trying: "در حال ترجمه…",
    tryNote: "یک بند، هر بار کسری از یک سنت. چیزی ذخیره نمی‌شود.",
    tryFailed: "ترجمهٔ بند انجام نشد.",
    tryLimits:
      "«آزمایش روی یک بند» همین بند را نگه می‌دارد، تا تنها چیزی که تغییر می‌کند دستور شما باشد؛ «بندی دیگر» بندی تازه از جایی دیگر در کتاب می‌آورد. نام‌ها را نخستین فراخوان ترجمهٔ واقعی تثبیت می‌کند، پس نمونه نمی‌تواند نشان دهد که نام‌ها چگونه خواهند بود.",
    howLinkedNote:
      "آنچه به مدل گفته می‌شود این کتاب چگونه باید خوانده شود. تغییرش دهید، روی یک بند بیازمایید، سپس کتاب را تازه کنید تا اعمال شود.",
    howSave: "ذخیره",
    howSaveNote: "ذخیره هنوز چیزی را تغییر نمی‌دهد؛ تازه‌کردن فشاری جداگانه است، پایین‌تر.",
    howSaveFailed: "دستور ذخیره نشد.",
    causeInstruction: "دستور تغییر کرده است، پس کل کتاب دوباره ترجمه می‌شود.",
    audioOutdated: (c) =>
      `${f(c)} فصلِ روایت‌شده باید دوباره ضبط شود — فقط بخش‌هایی که واژه‌هایشان عوض می‌شود هزینه دارد.`,
    conflictsHeading: "جاهایی که ترجمه هم‌نظر نبود",
    conflictsNote:
      "این‌ها را خودتان درست کرده‌اید و بعد متن اصلی عوض شده است. آنچه کتاب می‌گوید نوشتهٔ شماست؛ این فقط چیزی است که ترجمه می‌گفت.",
    conflictsNone: "چیزی برای بررسی نیست.",
    conflictsBadge: (c) => `${f(c)} مورد برای بررسی`,
    conflictSourceChanged: "آنچه در متن اصلی عوض شد",
    conflictYours: "نوشتهٔ شما",
    conflictProposed: "آنچه ترجمه می‌گفت",
    conflictKeep: "مالِ خودم بماند",
    conflictTake: "این را بگذار",
    conflictKeepAll: "همه مالِ خودم بماند",
    conflictTakeAll: "همه را بگذار",
    conflictSuperseded:
      "شما این واژه‌ها را دوباره نوشته‌اید، پس این مورد دیگر موضوعیت ندارد. برای پاک شدن، نگهش دارید.",
    conflictTitleUnit: "عنوان فصل",
    conflictChapterUnit: "کل فصل",
    conflictAudio: () =>
      "گذاشتن این‌ها به‌خودی‌خود چیزی را دوباره ضبط نمی‌کند، اما صدای فصل کهنه می‌شود — فقط بخش‌هایی که واژه‌هایشان عوض می‌شود هزینه دارد.",
    conflictFailed: "تسویهٔ این مورد ممکن نشد.",
    chaptersEdited: (c) =>
      `${f(c)} فصلی که خودتان ویرایش کرده‌اید دوباره ترجمه می‌شود. نوشتهٔ شما می‌ماند؛ می‌توانید ببینید ترجمه چه می‌گفت.`,
    howRefreshNote: "تا کتاب را تازه نکنید هیچ اتفاقی نمی‌افتد.",
    effortHeading: "میزان تفکر مدل",
    effortNote:
      "تفکر بیشتر معمولاً خوش‌خوان‌تر است و همیشه گران‌تر. بهای کنار دکمه با آن تغییر می‌کند.",
    effortLinkedNote:
      "این بر هر چیزی که تازه‌سازی بعدی ترجمه می‌کند اعمال می‌شود. به‌تنهایی چیزی را دوباره ترجمه نمی‌کند، پس با تغییر آن هیچ صدایی از دست نمی‌رود.",
    effortFast: "سریع",
    effortMedium: "متوسط",
    effortHigh: "زیاد",
    refreshHeading: "تازه‌سازی از منبع",
  },
  characters: {
    heading: "شخصیت‌ها",
    note: "مدل زبانی همهٔ فصل‌ها را می‌خواند و کسانی را که تکرار می‌شوند یا مهم‌اند فهرست می‌کند. هر یک معرفی‌ای دارد که خواننده پس از آشنایی با او می‌بیند، و توصیفی از کل داستانش که خواننده فقط اگر بخواهد داستان لو برود می‌بیند. بعد فهرست در اختیار شماست.",
    noText: "اول فصلی با متن بیفزایید.",
    extract: "استخراج شخصیت‌ها",
    extractAgain: "استخراج دوباره",
    narratorHeading: "راوی",
    narratorNote:
      "برای کتابی که یکی از شخصیت‌هایش روایتش می‌کند — به اول‌شخص، یا در قالب نامه. خواننده چهرهٔ راوی را هر جا که نقل‌قولی گفته نمی‌شود می‌بیند، از انتشار بعدی.",
    narratorNone: "هیچ‌کس",
    speakersHeading: "چه کسی سخن می‌گوید",
    manySpeak: "هر که نقل شده",
    oneSpeaker: "فقط راوی",
    oneSpeakerNote:
      "برای نامه، جستار یا خاطره: هر جملهٔ داخل گیومه از آنِ راوی است، پس گویندهٔ جمله‌ها هرگز پرسیده نمی‌شود و هزینه‌ای ندارد.",
    oneSpeakerNeedsNarrator: "اول راوی را انتخاب کنید.",
    oneSpeakerFailed: "تغییر گوینده انجام نشد.",
    extracting: "در حال استخراج…",
    estimate: (chapters, cost) => `خواندن ${f(chapters)} فصل حدود ${cost} هزینه دارد.`,
    stale: (chapters, cost) =>
      `${f(chapters)} فصل از آن زمان تغییر کرده — خواندن دوباره حدود ${cost} هزینه دارد.`,
    progress: (done, total) => `خواندن فصل ${f(done)} از ${f(total)}…`,
    merging: "در حال گردآوری شخصیت‌ها…",
    result: (count, chapters, cost) => `${f(count)} شخصیت از ${f(chapters)} فصل · ${cost}`,
    partial: (failed) => `${f(failed)} فصل خوانده نشد. فهرست از بقیهٔ فصل‌ها ساخته شده است.`,
    failed: "شخصیت‌ها استخراج نشدند.",
    empty: "هنوز شخصیتی نیست.",
    inChapters: (titles) => `در: ${titles}`,
    alsoKnownAs: (names) => `همچنین ${names}`,
    edited: "ویرایش‌شده",
    byAuthor: "افزودهٔ شما",
    edit: "ویرایش",
    add: "افزودن شخصیت",
    name: "نام",
    aliases: "نام‌های دیگر، جدا با ویرگول",
    role: "نقش",
    description: "توصیف: کل داستان او",
    introduction: "معرفی: او در نخستین دیدار کیست، بی آنچه بعد می‌آید",
    readersSee: "خواننده می‌بیند",
    forms: {
      heading: "شکل‌های دیگر",
      note: "یک تغییر چهره یا سنی دیگر — مینروا در هیئت منتس، قهرمانی چهل سال جوان‌تر. هر شکل چهرهٔ خودش را دارد، پس تصویرش از چهرهٔ خود شخصیت کشیده نمی‌شود.",
      add: "+ افزودن شکل",
      name: "نام این شکل",
      aliases: "نام‌هایی که متن برایش به کار می‌برد، جدا با ویرگول",
      appearance: "این شکل چه شکلی است",
      save: "ذخیره",
      cancel: "انصراف",
      remove: "حذف",
      saveFailed: "شکل ذخیره نشد.",
      of: (form, character) => `${form} — ${character} در شکلی دیگر`,
    },
    save: "ذخیره",
    saving: "در حال ذخیره…",
    saveFailed: "شخصیت ذخیره نشد.",
    fill: "پر کردن با هوش مصنوعی",
    filling: "در حال خواندن کتاب…",
    fillHint:
      "این نام را در کتاب پیدا می‌کند و خانه‌های خالی را پر می‌کند. تا ذخیره نکنید چیزی ذخیره نمی‌شود.",
    filled: (passages) => `از ${f(passages)} بخش کتاب پر شد، به بهای`,
    fillFailed: "پر کردن این شخصیت ممکن نشد.",
    delete: "حذف شخصیت",
    deleteQuestion: "این شخصیت حذف شود؟ استخراج دوباره آن را برنمی‌گرداند.",
    mergeInto: "ادغام در شخصیتی دیگر",
    mergeChoose: "شخصیتی را انتخاب کنید…",
    merge: "ادغام",
    extractFailed: "استخراج شروع نشد.",
    loadFailed: "شخصیت‌ها بارگذاری نشدند.",
  },
  dialogue: {
    heading: "چه کسی هر جمله را می‌گوید",
    note: "هر جملهٔ داخل گیومه، وقتی بخواهید، به شخصیتی که آن را می‌گوید نسبت داده می‌شود — اول شخصیت‌ها را بررسی کنید، چون جمله‌ها به آن‌ها داده می‌شوند. جمله‌های کسانی که در فهرست شخصیت‌ها نیستند «کسی دیگر» علامت می‌خورند. هر جمله را می‌توانید اصلاح کنید.",
    unavailable: "انتساب گفت‌وگوها اینجا روشن نیست.",
    modelHeading: "مدل",
    modelDefault: "پیش‌فرض",
    modelNote: "عوض کردنش چیزی را شروع نمی‌کند؛ اجرای بعدی از آن استفاده می‌کند.",
    modelFailed: "مدل عوض نشد.",
    modelUnavailable: "مدل این کتاب اینجا در دسترس نیست.",
    progress: (done, total) => `یافتن گوینده‌ها: فصل ${f(done)} از ${f(total)}…`,
    result: (lines, chapters, cost) => `${f(lines)} جمله در ${f(chapters)} فصل · ${cost}`,
    changed: (chapters, cost) =>
      `${f(chapters)} فصل از آن زمان تغییر کرده — به‌روزرسانی حداکثر ${cost} هزینه دارد.`,
    first: (cost) =>
      `هنوز پرسیده نشده. اول شخصیت‌ها را بررسی کنید: هر جمله به یکی از آن‌ها داده می‌شود — حداکثر ${cost}.`,
    find: (chapters) => `یافتن گوینده‌ها · ${f(chapters)} فصل`,
    update: "به‌روزرسانی",
    updating: "در حال به‌روزرسانی…",
    updateFailed: "به‌روزرسانی شروع نشد.",
    again: (cost) =>
      `همهٔ فصل‌ها با شخصیت‌های کنونی دوباره پرسیده شوند و اصلاح‌های شما بمانند — حداکثر ${cost} هزینه دارد.`,
    askAgain: "پرسیدن دوباره",
    askingAgain: "در حال پرسیدن…",
    againFailed: "پرسیدن دوباره شروع نشد.",
    speakersFound: (kept) =>
      kept === 0
        ? "هیچ گویندهٔ جاافتاده‌ای دو جمله یا بیشتر نداشت."
        : `${f(kept)} گوینده با دو جمله یا بیشتر به شخصیت‌ها افزوده شد.`,
    refused: "شروع نشد: بودجهٔ این ماه کافی نیست.",
    failed: "گوینده‌ها یافته نشدند.",
    partial: (failed) => `${f(failed)} فصل انجام نشد.`,
    lines: (count) => `${f(count)} جمله`,
    findLines: "یافتن جمله‌هایش",
    findingLines: "در حال پرسیدن…",
    someoneElse: "کسی دیگر",
    notSpeech: "گفتار نیست",
    unknown: "نامعلوم",
    byYou: "تعیین‌شده توسط شما",
    choose: "چه کسی این را می‌گوید؟",
    saidBy: (name) => `گفتهٔ ${name}`,
    correctFailed: "گوینده تغییر نکرد.",
    loadFailed: "جمله‌ها بارگذاری نشدند.",
    oneSpeaker: (name) =>
      `یک گوینده: همهٔ جمله‌ها از آنِ ${name} است. چیزی پرسیده نمی‌شود و هزینه‌ای ندارد.`,
  },
  usage: {
    title: "مصرف",
    resets: (period) => `${period} · اول هر ماه از نو شروع می‌شود`,
    thisMonth: "این ماه",
    loadFailed: "مصرف بارگذاری نشد.",
    of: "از",
    budgetUsed: "بودجهٔ مصرف‌شدهٔ این ماه",
    left: (amount) => `${amount} از بودجهٔ این ماه برای روایت و پرسش باقی است.`,
    wentOn: "صرف چه شد",
    nothingSpent: "این ماه هنوز هزینه‌ای نشده است.",
    recent: "هزینه‌های اخیر",
    others: (mine) =>
      `هزینه‌های خود این حساب ${mine} است. بقیه را اعضای دیگر همین کتابخانه خرج کرده‌اند.`,
    kinds: {
      search_index: "نمایهٔ جستجوی کتاب",
      narration: "روایت",
      preview: "پیش‌شنود تلفظ",
      word_timing: "زمان‌بندی واژه‌ها",
      rewrite: "بازنویسی با هوش مصنوعی",
      question: "پرسش",
      characters: "شخصیت‌ها",
      dialogue: "گویندهٔ جمله‌ها",
      pronunciation: "تلفظ برای الون‌لبز",
      illustration: "تصویرها",
      translation: "ترجمه",
      voice_hearing: "شنیدن صداها برای انتخاب بازیگر",
    },
  },
  admin: {
    title: "مدیریت",
    back: "مدیریت",
    accounts: (c) => `${f(c)} حساب · نقش‌ها، هزینهٔ این ماه و سقف آن`,
    accountsFallback: "حساب‌ها",
    loadFailed: "حساب‌ها بارگذاری نشدند.",
    badge: "مدیر",
    spent: (usd) => `${usd} در این ماه`,
    limit: (usd) => `سقف ${usd}`,
    limitTitle: "سقف ماهانه",
    limitWhat:
      "کتابخانهٔ این حساب در هر ماه تا این اندازه می‌تواند هزینه کند. تغییر از هزینهٔ بعدی اعمال می‌شود و چیزی را برنمی‌گرداند.",
    limitNone: "این حساب کتابخانه‌ای ندارد، پس چیزی برای محدود کردن نیست.",
    limitFor: (name) => `سقف ماهانهٔ ${name}`,
    limitFailed: "سقف تغییر نکرد.",
    limitStop: "توقف",
    limitOther: "مقدار دیگر",
    limitOtherLabel: "مقدار دیگر، به دلار",
    limitSave: "ذخیره",
    limitInvalid: "این یک مبلغ نیست.",
    roleFailed: "نقش تغییر نکرد.",
    localeFailed: "زبان تغییر نکرد.",
    roleFor: (name) => `نقش ${name}`,
    languageFor: (name) => `زبان ${name}`,
    noLibrary: "بدون کتابخانه",
    roles: { reader: "خواننده", creator: "نویسنده", admin: "مدیر" },
    wayIn: { google: "گوگل", apple: "اپل", tester: "آزمایشگر" },
    off: "خاموش",
    switchOff: "خاموش کن",
    switchOn: "روشن کن",
    switchFailed: "حساب تغییر نکرد.",
    testerTitle: "ورود تازهٔ آزمایشگر",
    testerWhat:
      "یک نام کاربری و گذرواژه‌ای ساخته‌شده، برای آزمایشگر یا بازبین. ایمیلی گرفته نمی‌شود. گذرواژه فقط یک بار نشان داده می‌شود.",
    testerUsername: "نام کاربری",
    testerCreate: "بساز",
    testerFailed: "آزمایشگر ساخته نشد.",
    recordsTitle: "مطالعه",
    recordsNone: "هنوز زمانی ثبت نشده.",
    minutes: (m) => `${f(m)} دقیقه`,
    questionKinds: { typed: "تایپ‌شده", meaning: "معنی", explain: "توضیح", simpler: "ساده‌تر" },
    questionsTitle: "پرسش‌ها، از آغاز",
    sessionsTitle: "نشست‌ها، از آغاز",
    sessionsNone: "هنوز نشست مطالعه‌ای ثبت نشده.",
    sessionsCount: "نشست",
    sessionsMedian: "میانه",
    sessionsLongest: "بلندترین",
    sessionsEndedBy: {
      switched_away: "رفت به برنامهٔ دیگر",
      left: "کتاب را بست",
      idle: "بی‌کار ماند",
    },
    sessionsBook: (count, median) => `${f(count)} نشست · میانه ${median}`,
    duration: (seconds) =>
      seconds < 60
        ? `${f(seconds)} ثانیه`
        : seconds < 3600
          ? `${f(Math.round(seconds / 60))} دقیقه`
          : `${f(Math.floor(Math.round(seconds / 60) / 60))} ساعت ${f(Math.round(seconds / 60) % 60)} دقیقه`,
    testerMade: (username) =>
      `${username} با این گذرواژه در اپ وارد می‌شود. همین حالا رونوشت بگیرید: دوباره نشان داده نمی‌شود.`,
  },
  legal: {
    privacy: "حریم خصوصی",
    terms: "شرایط استفاده",
    about: "درباره",
  },
  traffic: {
    title: "بازدیدکنندگان",
    link: "بازدیدکنندگان",
    what: "۳۰ روز گذشته، به تفکیک جایی که آمده‌اند. بدون کوکی شمرده می‌شود؛ هر دستگاه روزی یک بار.",
    loadFailed: "آمار بارگذاری نشد.",
    empty:
      "هنوز بازدیدی شمرده نشده. پیوندهای پست‌ها باید ‎?src=‎ داشته باشند، مثلاً ‎?src=reddit-hamlet‎.",
    source: "منبع",
    visitors: "بازدید",
    returned: "برگشتند",
    signups: "ثبت‌نام",
    total: "جمع",
    cohortsTitle: "هفته‌های ثبت‌نام",
    cohortsWhat: "ثبت‌نام‌های هر هفته به تفکیک منبع، و میانهٔ نشست مطالعهٔ همان خوانندگان.",
    cohortsEmpty: "در این بازه ثبت‌نامی نیست.",
    week: "هفته",
    medianSession: "میانهٔ نشست",
  },
  errors: {
    title: "خطاها",
    link: "خطاها",
    what: "خطاهای سرور در ۱۴ روز گذشته، تازه‌ترین اول. شناسهٔ ارجاعی که خواننده می‌فرستد، یا یک اعلان، اینجا پیدا می‌شود.",
    loadFailed: "خطاها بارگذاری نشد.",
    empty: "در ۱۴ روز گذشته خطای سروری نبوده.",
    user: "حساب",
  },
  collections: {
    row: "مجموعه‌ها",
    books: (count: number) => `${f(count)} کتاب`,
    loadFailed: "این مجموعه بارگذاری نشد.",
    notFound: "در این مجموعه کتابی برای خواندن شما نیست.",
    back: "همهٔ کتاب‌ها",
    link: "مجموعه‌ها",
    title: "مجموعه‌ها",
    what: "فهرست‌های گزیده‌ای که بالای «همهٔ کتاب‌ها» می‌آیند. هر خواننده فقط کتاب‌هایی را می‌بیند که اجازهٔ خواندنشان را دارد؛ مجموعه‌ای که برای او کتابی ندارد پنهان است.",
    listFailed: "مجموعه‌ها بارگذاری نشد.",
    empty: "هنوز مجموعه‌ای نیست.",
    name: "نام",
    description: "توضیح",
    create: "ساختن مجموعه",
    save: "ذخیره",
    delete: "حذف",
    deleteQuestion: (name: string) => `«${name}» حذف شود؟ کتاب‌هایش سر جایشان می‌مانند.`,
    noBooks: "هنوز کتابی در این مجموعه نیست.",
    addBook: "افزودن کتاب",
    findBook: "کتاب را با عنوانش پیدا کنید یا پیوندش را بچسبانید",
    addThis: "افزودن این کتاب",
    moveUp: "بالاتر",
    moveDown: "پایین‌تر",
    remove: "برداشتن",
    draft: "منتشرنشده",
    notPublic: (visibility: string) => (visibility === "shared" ? "هم‌رسانی‌شده" : "خصوصی"),
    kind: "گونه",
    kindCollection: "مجموعه",
    kindSeries: "مجموعهٔ چندجلدی",
  },
  series: {
    volumeOf: (number: number, of: number) => `جلد ${f(number)} از ${f(of)}`,
    volumes: (count: number) => `${f(count)} جلد`,
    previous: "جلد قبلی",
    next: "جلد بعدی",
  },
  activity: {
    title: "فعالیت",
    link: "فعالیت",
    what: "آنچه همین حالا در حال اجراست و آنچه هزینه می‌برد. خودش به‌روز می‌شود.",
    updated: (time: string) => `به‌روز شده ${time}`,
    loadFailed: "فعالیت بارگذاری نشد.",
    attention: "نیاز به توجه",
    allClear: "چیزی نیاز به توجه ندارد.",
    machines: "ماشین‌ها",
    noMachines: "هیچ ماشینی در حال اجرا نیست.",
    stoppedToday: "امروز متوقف شده",
    work: "کارها",
    noWork: "هیچ کاری در حال اجرا یا در صف نیست.",
    other: "سایر",
    status: {
      starting: "در حال راه‌اندازی",
      running: "در حال اجرا",
      stopped: "متوقف",
    } as Record<string, string>,
    lane: {
      inline: "سرور",
      gpu: "کارت گرافیک",
      kokoro: "کوکورو",
      portrait: "چهره",
    } as Record<string, string>,
    kind: {
      "render.plan": "برنامه‌ریزی روایت",
      "render.chunk": "روایت",
      "align.chunk": "هم‌ترازسازی",
      "render.finalize": "پایان روایت",
      "batch.submit": "ارسال به حالت اقتصادی",
      "batch.collect": "دریافت از حالت اقتصادی",
      "export.chapter": "ساختن فایل دانلود",
      "narrate.book": "روایت کتاب",
      "prepare.piece": "آماده‌سازی متن",
      "performance_script.chapter": "آماده‌سازی برای صداها",
      "reading.index": "نمایه‌سازی برای جست‌وجو",
      "translation.step": "ترجمه",
      "characters.extract": "یافتن شخصیت‌ها",
      "characters.merge": "ساختن فهرست شخصیت‌ها",
      "characters.attribute": "گوینده‌ی هر سطر",
      "illustration.plan": "برنامه‌ریزی تصویرها",
      "illustration.spot": "کشیدن تصویرها",
      "illustration.portrait": "کشیدن چهره‌ها",
      "illustration.live_portrait": "ساختن چهره‌های زنده",
      "noop.echo": "آزمایش صف",
    } as Record<string, string>,
    duration: (seconds: number) => {
      if (seconds < 60) return `${f(seconds)} ثانیه`;
      const minutes = Math.floor(seconds / 60);
      return minutes >= 60
        ? `${f(Math.floor(minutes / 60))} ساعت ${f(minutes % 60)} دقیقه`
        : `${f(minutes)} دقیقه`;
    },
    up: (d: string) => `${d} روشن`,
    ranFor: (d: string) => `${d} کار کرد`,
    soFar: (cost: string) => `تا این لحظه ${cost}`,
    spent: (cost: string) => `${cost}`,
    perHour: (rate: string) => `ساعتی ${rate}`,
    seen: (d: string) => `${d} پیش دیده شد`,
    neverSeen: "هنوز دیده نشده",
    jobs: (count: number) => `${f(count)} کار`,
    stopReason: (reason: string) => `متوقف شد: ${reason}`,
    running: (count: number) => `${f(count)} در حال اجرا`,
    waiting: (count: number) => `${f(count)} در صف`,
    failed: (count: number) => `${f(count)} ناموفق`,
    oldest: (d: string) => `قدیمی‌ترین ${d} در صف`,
  },
  device: {
    title: "ورود از خط فرمان",
    what: "چیزی روی یک رایانه می‌خواهد از طریق sageleaf.io به جای شما کار کند. فقط اگر همین حالا خودتان آن را اجرا کرده‌اید تأیید کنید.",
    code: "کد",
    asking: "درخواست‌کننده",
    requested: (minutes: number) =>
      minutes < 1 ? "همین حالا درخواست شد" : `${f(minutes)} دقیقه پیش درخواست شد`,
    approve: "تأیید",
    deny: "رد",
    approved: "تأیید شد. خط فرمان وارد شد؛ می‌توانید این صفحه را ببندید.",
    denied: "رد شد. هیچ ورودی انجام نشد.",
    notFound: "این کد ناشناخته است، منقضی شده، یا قبلاً پاسخ داده شده.",
    noCode: "پیوندی را که خط فرمان چاپ کرد باز کنید.",
    failed: "انجام نشد.",
    signIn: "برای تأیید وارد شوید",
  },
  install: {
    prompt: "این را در اپ سیج‌لیف بخوانید",
    get: "دریافت اپ",
    dismiss: "حالا نه",
    appStore: "دریافت از اپ استور",
    googlePlay: "دریافت از گوگل پلی",
  },
};

export const messages: Record<Locale, Messages> = { en, fa };

export function isLocale(value: unknown): value is Locale {
  return value === "en" || value === "fa";
}
